/**
 * Props.js - biblioteca de objetos do cenario de "Operacao Blackout".
 *
 * Cada funcao make*() devolve um THREE.Object3D montado na ORIGEM, em escala de
 * metros, pronto para o Level posicionar. O Level depois "achata" esses grupos
 * dentro do batcher (merge por material), entao aqui NAO se preocupa com draw
 * call: preocupa-se com detalhe.
 *
 * Convencoes que o batcher do Level le de cada Mesh:
 *   userData.mat      -> nome do material (chave de merge; tambem vira surface)
 *   userData.solid    -> entra na colisao (default true)
 *   userData.cast     -> castShadow (default true)
 *   userData.receive  -> receiveShadow (default true)
 *   userData.uv       -> 'world' (reprojecao triplanar, default) ou 'keep'
 *   userData.uvScale  -> [su, sv] quando uv === 'keep'
 *   userData.dynamic  -> nao mergeia; fica como objeto vivo (ventilador girando)
 *
 * Regra de acabamento: NENHUMA caixa lisa. Todo prop tem quina quebrada,
 * moldura, rebite, nervura ou friso. Caixa lisa e o que entrega "demo tecnico".
 *
 * Nada aqui aloca objeto em tempo de frame - tudo roda uma vez, no init.
 */

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// ---------------------------------------------------------------------------
// utilitarios
// ---------------------------------------------------------------------------

/** PRNG deterministico (mulberry32). Mapa igual em toda execucao - o revisor
 *  visual compara prints entre rodadas, entao aleatorio "de verdade" e veneno. */
export function makeRng(seed = 1337) {
  let a = seed >>> 0;
  return function rng() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Numero no intervalo [lo, hi). */
export function rand(rng, lo, hi) {
  return lo + (hi - lo) * rng();
}

/** Cria a malha ja marcada para o batcher. */
export function part(mats, matName, geometry, opts) {
  const mesh = new THREE.Mesh(geometry, mats.get(matName));
  const u = mesh.userData;
  u.mat = matName;
  u.surface = matName;
  u.solid = !opts || opts.solid !== false;
  u.cast = !opts || opts.cast !== false;
  u.receive = !opts || opts.receive !== false;
  u.uv = (opts && opts.uv) || 'world';
  if (opts && opts.uvScale) u.uvScale = opts.uvScale;
  if (opts && opts.dynamic) u.dynamic = true;
  return mesh;
}

/**
 * Caixa com as arestas CORTADAS (chanfro reto), 44 triangulos.
 *
 * Por que nao RoundedBoxGeometry: ela custa 108 triangulos por caixa com
 * segments=1, e o cenario tem alguns milhares de caixinhas. O chanfro reto
 * resolve o que interessa - a aresta viva pegando a luz do sol rasante, que e o
 * que separa "objeto" de "caixa de teste" - a 40% do custo.
 */
export function chamferBoxGeometry(w, h, d, bevel) {
  const b = Math.max(0.002, Math.min(bevel, Math.min(w, Math.min(h, d)) * 0.45));
  const hx = w * 0.5, hy = h * 0.5, hz = d * 0.5;
  const ax = hx - b, ay = hy - b, az = hz - b;
  const out = [];
  const tri = (p, q, r) => {
    const ux = q[0] - p[0], uy = q[1] - p[1], uz = q[2] - p[2];
    const vx = r[0] - p[0], vy = r[1] - p[1], vz = r[2] - p[2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const cx = p[0] + q[0] + r[0], cy = p[1] + q[1] + r[1], cz = p[2] + q[2] + r[2];
    // solido convexo centrado na origem: normal para fora aponta junto do centroide
    if (nx * cx + ny * cy + nz * cz >= 0) out.push(p, q, r);
    else out.push(p, r, q);
  };
  const quad = (p, q, r, s) => { tri(p, q, r); tri(p, r, s); };

  for (const s of [-1, 1]) {
    quad([s * hx, -ay, -az], [s * hx, ay, -az], [s * hx, ay, az], [s * hx, -ay, az]);
    quad([-ax, s * hy, -az], [ax, s * hy, -az], [ax, s * hy, az], [-ax, s * hy, az]);
    quad([-ax, -ay, s * hz], [ax, -ay, s * hz], [ax, ay, s * hz], [-ax, ay, s * hz]);
  }
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      quad([sx * hx, sy * ay, -az], [sx * hx, sy * ay, az],
        [sx * ax, sy * hy, az], [sx * ax, sy * hy, -az]);
    }
    for (const sz of [-1, 1]) {
      quad([sx * hx, -ay, sz * az], [sx * hx, ay, sz * az],
        [sx * ax, ay, sz * hz], [sx * ax, -ay, sz * hz]);
    }
  }
  for (const sy of [-1, 1]) {
    for (const sz of [-1, 1]) {
      quad([-ax, sy * hy, sz * az], [ax, sy * hy, sz * az],
        [ax, sy * ay, sz * hz], [-ax, sy * ay, sz * hz]);
    }
  }
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        tri([sx * hx, sy * ay, sz * az], [sx * ax, sy * hy, sz * az], [sx * ax, sy * ay, sz * hz]);
      }
    }
  }

  const pos = new Float32Array(out.length * 3);
  for (let i = 0; i < out.length; i++) {
    pos[i * 3] = out[i][0];
    pos[i * 3 + 1] = out[i][1];
    pos[i * 3 + 2] = out[i][2];
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.computeVertexNormals();   // nao indexada -> normal por face, sombreado duro
  const nor = g.attributes.normal;
  const uv = new Float32Array(out.length * 2);
  for (let i = 0; i < out.length; i++) {
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    const px = pos[i * 3], py = pos[i * 3 + 1], pz = pos[i * 3 + 2];
    if (ny >= nx && ny >= nz) { uv[i * 2] = px; uv[i * 2 + 1] = pz; }
    else if (nx >= nz) { uv[i * 2] = pz; uv[i * 2 + 1] = py; }
    else { uv[i * 2] = px; uv[i * 2 + 1] = py; }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return g;
}

/** Caixa simples (ou chanfrada, com opts.bevel). */
export function box(mats, matName, w, h, d, x, y, z, opts) {
  const bev = opts && opts.bevel;
  const g = bev ? chamferBoxGeometry(w, h, d, bev) : new THREE.BoxGeometry(w, h, d);
  const m = part(mats, matName, g, opts);
  m.position.set(x, y, z);
  if (opts && opts.rot) m.rotation.set(opts.rot[0], opts.rot[1], opts.rot[2]);
  return m;
}

/** Cilindro em pe (eixo Y). */
export function cyl(mats, matName, rTop, rBot, h, seg, x, y, z, opts) {
  const g = new THREE.CylinderGeometry(rTop, rBot, h, seg || 12, 1, (opts && opts.open) === true);
  const m = part(mats, matName, g, opts);
  m.position.set(x, y, z);
  if (opts && opts.rot) m.rotation.set(opts.rot[0], opts.rot[1], opts.rot[2]);
  return m;
}

/** Anel/arruela horizontal (usado como cinta de tambor, aro, colar de poste). */
export function ring(mats, matName, r, tube, x, y, z, opts) {
  const g = new THREE.TorusGeometry(r, tube, 4, (opts && opts.seg) || 10);
  const m = part(mats, matName, g, opts);
  m.position.set(x, y, z);
  // torus nasce no plano XY; por padrao deitamos (aro no chao). upright mantem
  // de pe, para grelha de ventilador e outras coisas presas em parede.
  if (!opts || opts.upright !== true) m.rotation.x = Math.PI / 2;
  return m;
}

/** Devolve uma copia da geometria com as faces invertidas (verso de tecido). */
export function flipped(geometry) {
  const src = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const pos = src.attributes.position.array;
  for (let i = 0; i < pos.length; i += 9) {
    for (let k = 0; k < 3; k++) {
      const t = pos[i + 3 + k];
      pos[i + 3 + k] = pos[i + 6 + k];
      pos[i + 6 + k] = t;
    }
  }
  const nor = src.attributes.normal ? src.attributes.normal.array : null;
  if (nor) for (let i = 0; i < nor.length; i++) nor[i] = -nor[i];
  const uv = src.attributes.uv ? src.attributes.uv.array : null;
  if (uv) {
    for (let i = 0; i < uv.length; i += 6) {
      for (let k = 0; k < 2; k++) {
        const t = uv[i + 2 + k];
        uv[i + 2 + k] = uv[i + 4 + k];
        uv[i + 4 + k] = t;
      }
    }
  }
  src.attributes.position.needsUpdate = true;
  return src;
}

/**
 * Pano fino: gera a folha e o verso invertido, para o tecido ser visivel dos
 * dois lados sem depender de material DoubleSide (que nao e nosso).
 */
export function cloth(group, mats, matName, geometry, opts) {
  const o = Object.assign({ uv: 'keep', uvScale: [2, 2], solid: false }, opts || {});
  group.add(part(mats, matName, geometry, o));
  group.add(part(mats, matName, flipped(geometry), o));
}

/**
 * Desloca a UV da geometria. Sem isso, dois panos do mesmo tamanho caem na
 * MESMA regiao da textura e saem com a listra na mesma fase - foi o que fez as
 * oito pecas do varal e os quatro toldos do beco lerem como copias.
 */
export function offsetUV(geometry, du, dv) {
  const uv = geometry.attributes.uv;
  if (!uv) return geometry;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) + du, uv.getY(i) + dv);
  uv.needsUpdate = true;
  return geometry;
}

/**
 * Painel de pano: barriga em Z, bainha inferior ONDULADA (a bainha reta e o que
 * fazia a roupa ler como bandeirola) e UV proporcional ao tamanho real.
 * O painel nasce centrado na origem, no plano XY.
 */
export function clothPanel(w, h, rng, o) {
  const opt = o || {};
  const segX = opt.segX || 5;
  const segY = opt.segY || 4;
  const g = new THREE.PlaneGeometry(w, h, segX, segY);
  const p = g.attributes.position;
  const phase = rand(rng, 0, 6.28);
  const amp = opt.billow === undefined ? 0.045 : opt.billow;
  const hem = opt.hem === undefined ? 0.055 : opt.hem;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const u = x / w + 0.5;
    const v = y / h + 0.5;
    p.setZ(i, Math.sin(u * 5.6 + phase) * amp * (1.05 - v) + Math.cos(v * 3.1 + phase) * amp * 0.4);
    // bainha: so a fileira de baixo desce, em onda
    if (v < 0.001) p.setY(i, y - Math.abs(Math.sin(u * 4.2 + phase * 1.7)) * hem);
  }
  g.computeVertexNormals();
  return g;
}

/** Extrusao de um perfil 2D (XY) ao longo de Z. Perfis: barreira, capo, lataria. */
export function extrude(mats, matName, shape, depth, opts) {
  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: (opts && opts.bevel) !== undefined ? opts.bevel > 0 : true,
    bevelSize: (opts && opts.bevel) || 0.02,
    bevelThickness: (opts && opts.bevel) || 0.02,
    bevelSegments: 1,
    steps: 1,
    curveSegments: (opts && opts.curveSegments) || 6
  });
  g.translate(0, 0, -depth * 0.5);
  const m = part(mats, matName, g, opts);
  return m;
}

// ---------------------------------------------------------------------------
// esquadrias - janela, porta, portao de enrolar
// ---------------------------------------------------------------------------

/**
 * Moldura de janela. Construida virada para +Z (a parede fica em z <= 0).
 * Estilos: 'glass' (vidro sujo), 'dark' (vao escuro), 'boarded' (tapada com
 * tabuas), 'shutter' (persiana metalica meio fechada).
 * Sempre tem peitoril saliente e requadro rebaixado - e o rebaixo que da
 * sombra propria e faz a fachada parecer construida, nao pintada.
 */
export function makeWindowUnit(mats, o) {
  const w = o.w, h = o.h;
  const t = o.wallT || 0.4;
  const rev = 0.22;                    // profundidade do requadro
  const g = new THREE.Group();
  const hw = w * 0.5, hh = h * 0.5;

  // requadro (jamba superior, inferior e laterais), recuado para dentro
  const jt = 0.1;
  g.add(box(mats, o.trim || 'concrete', w + jt * 2, jt, rev, 0, hh + jt * 0.5, -rev * 0.5));
  g.add(box(mats, o.trim || 'concrete', w + jt * 2, jt, rev, 0, -hh - jt * 0.5, -rev * 0.5));
  g.add(box(mats, o.trim || 'concrete', jt, h + jt * 2, rev, -hw - jt * 0.5, 0, -rev * 0.5));
  g.add(box(mats, o.trim || 'concrete', jt, h + jt * 2, rev, hw + jt * 0.5, 0, -rev * 0.5));

  // verga (lintel) saliente e peitoril com pingadeira
  g.add(box(mats, o.trim || 'concrete', w + 0.5, 0.14, 0.14, 0, hh + 0.17, 0.03, { bevel: 0.03 }));
  g.add(box(mats, o.trim || 'concrete', w + 0.42, 0.09, 0.3, 0, -hh - 0.12, 0.06, { bevel: 0.025 }));
  g.add(box(mats, o.trim || 'concrete', w + 0.42, 0.05, 0.06, 0, -hh - 0.19, 0.16));

  const style = o.style || 'dark';
  const back = -rev - 0.02;

  // Fundo cego: sem ele o predio macico vira um buraco que atravessa a massa e
  // deixa ver a parede oposta. Quem tem interior de verdade passa open:true.
  if (!o.open) {
    g.add(box(mats, 'plaster', w + 0.24, h + 0.24, 0.08, 0, 0, back - 0.36,
      { cast: false, solid: false }));
  }

  if (style === 'glass') {
    // caixilho + duas folhas de vidro
    const fr = 0.05;
    g.add(box(mats, 'wood', w, fr, 0.06, 0, hh - fr * 0.5, back + 0.06));
    g.add(box(mats, 'wood', w, fr, 0.06, 0, -hh + fr * 0.5, back + 0.06));
    g.add(box(mats, 'wood', fr, h, 0.06, -hw + fr * 0.5, 0, back + 0.06));
    g.add(box(mats, 'wood', fr, h, 0.06, hw - fr * 0.5, 0, back + 0.06));
    g.add(box(mats, 'wood', 0.045, h, 0.05, 0, 0, back + 0.06));
    g.add(box(mats, 'wood', w, 0.045, 0.05, 0, 0, back + 0.06));
    g.add(box(mats, 'glass', w - 0.08, h - 0.08, 0.03, 0, 0, back + 0.03,
      { cast: false, solid: false }));
  } else if (style === 'boarded') {
    // tabuas atravessadas, angulos diferentes - nada de grade regular
    const n = 4;
    for (let i = 0; i < n; i++) {
      const yy = -hh + (h * (i + 0.5)) / n;
      const ang = (i % 2 === 0 ? 1 : -1) * (0.05 + i * 0.018);
      g.add(box(mats, 'wood', w + 0.22, 0.17, 0.05, 0, yy, back + 0.05,
        { rot: [0, 0, ang], bevel: 0.012 }));
    }
    g.add(box(mats, 'metal_thin', 0.06, h * 0.8, 0.04, hw * 0.35, 0, back + 0.09));
  } else if (style === 'shutter') {
    // persiana de enrolar meio descida: corpo com nervuras + caixa superior
    const drop = h * (o.drop || 0.62);
    g.add(box(mats, 'metal_thin', w, 0.16, 0.14, 0, hh - 0.06, back + 0.08));
    const nr = Math.max(3, Math.floor(drop / 0.09));
    for (let i = 0; i < nr; i++) {
      g.add(box(mats, 'metal_thin', w, 0.075, 0.05, 0, hh - 0.16 - i * 0.088, back + 0.06,
        { bevel: 0.012 }));
    }
    g.add(box(mats, 'metal_thin', w + 0.04, 0.06, 0.07, 0, hh - 0.16 - nr * 0.088, back + 0.07));
  } else {
    // vao escuro: so sobra pedaco de caixilho quebrado
    g.add(box(mats, 'wood', w, 0.05, 0.05, 0, hh - 0.03, back + 0.05, { solid: false }));
    g.add(box(mats, 'wood', 0.05, h * 0.55, 0.05, -hw + 0.04, -hh + h * 0.28, back + 0.05,
      { solid: false }));
  }

  if (o.bars) {
    /**
     * Gradil de ferro. As barras eram de 2,5 cm espacadas 22 cm: a 20 m de
     * distancia isso da menos de um pixel por barra e a janela inteira ferve em
     * codigo de barras (o SMAA e antialias de BORDA, nao resolve frequencia
     * geometrica acima de Nyquist). Barra de 3,4 cm a cada 34 cm tem metade da
     * frequencia e o dobro da massa - continua lendo como grade de perto e para
     * de cintilar de longe.
     */
    const nb = Math.max(3, Math.round(w / 0.34));
    for (let i = 0; i < nb; i++) {
      const xx = -hw + 0.09 + (i * (w - 0.18)) / (nb - 1);
      g.add(box(mats, 'metal_thin', 0.034, h - 0.06, 0.032, xx, 0, 0.025,
        { solid: false, bevel: 0.008 }));
    }
    g.add(box(mats, 'metal_thin', w - 0.05, 0.042, 0.04, 0, h * 0.16, 0.025, { solid: false }));
    g.add(box(mats, 'metal_thin', w - 0.05, 0.042, 0.04, 0, -h * 0.2, 0.025, { solid: false }));
  }
  return g;
}

/** Vao de porta com batente, soleira e (opcional) folha de madeira entreaberta. */
export function makeDoorUnit(mats, o) {
  const w = o.w, h = o.h;
  const rev = 0.2;
  const g = new THREE.Group();
  const hw = w * 0.5;

  g.add(box(mats, 'concrete', w + 0.22, 0.11, rev, 0, h + 0.05, -rev * 0.5));
  g.add(box(mats, 'concrete', 0.11, h, rev, -hw - 0.055, h * 0.5, -rev * 0.5));
  g.add(box(mats, 'concrete', 0.11, h, rev, hw + 0.055, h * 0.5, -rev * 0.5));
  // verga saliente e soleira gasta
  g.add(box(mats, 'concrete', w + 0.6, 0.16, 0.16, 0, h + 0.18, 0.04, { bevel: 0.03 }));
  g.add(box(mats, 'concrete', w + 0.3, 0.08, 0.36, 0, 0.04, 0.06, { bevel: 0.02 }));

  if (o.style === 'wood') {
    const ang = o.ajar !== undefined ? o.ajar : 0.55;
    const leaf = new THREE.Group();
    const lw = w - 0.06;
    leaf.add(box(mats, 'wood', lw, h - 0.08, 0.06, lw * 0.5, 0, 0, { bevel: 0.012 }));
    // almofadas: dois quadros salientes
    leaf.add(box(mats, 'wood', lw * 0.62, (h - 0.08) * 0.34, 0.03, lw * 0.5, (h - 0.08) * 0.22, 0.04));
    leaf.add(box(mats, 'wood', lw * 0.62, (h - 0.08) * 0.34, 0.03, lw * 0.5, -(h - 0.08) * 0.2, 0.04));
    leaf.add(cyl(mats, 'metal', 0.035, 0.035, 0.12, 8, lw * 0.92, 0, 0.08,
      { rot: [Math.PI / 2, 0, 0], solid: false }));
    leaf.position.set(-hw + 0.03, h * 0.5 + 0.04, -0.04);
    leaf.rotation.y = ang;
    g.add(leaf);
  } else if (o.style === 'metal') {
    const leaf = new THREE.Group();
    const lw = w - 0.06;
    leaf.add(box(mats, 'metal_thin', lw, h - 0.08, 0.05, lw * 0.5, 0, 0, { bevel: 0.01 }));
    for (let i = 0; i < 3; i++) {
      leaf.add(box(mats, 'metal_thin', lw - 0.1, 0.04, 0.02, lw * 0.5, -0.5 + i * 0.5, 0.035));
    }
    leaf.position.set(-hw + 0.03, h * 0.5 + 0.04, -0.04);
    leaf.rotation.y = o.ajar !== undefined ? o.ajar : 0.22;
    g.add(leaf);
  }
  return g;
}

/** Portao de enrolar de galpao/garagem, parcialmente aberto. */
export function makeRollUpDoor(mats, o) {
  const w = o.w, h = o.h;
  const open = o.open !== undefined ? o.open : 0.45;   // fracao aberta
  const g = new THREE.Group();
  const drop = h * (1 - open);
  const hw = w * 0.5;

  // guias laterais e caixa do tambor
  g.add(box(mats, 'metal_thin', 0.12, h + 0.2, 0.16, -hw - 0.06, (h + 0.2) * 0.5, -0.02));
  g.add(box(mats, 'metal_thin', 0.12, h + 0.2, 0.16, hw + 0.06, (h + 0.2) * 0.5, -0.02));
  g.add(box(mats, 'metal_thin', w + 0.34, 0.3, 0.28, 0, h + 0.16, -0.02, { bevel: 0.03 }));

  const nr = Math.max(2, Math.floor(drop / 0.11));
  for (let i = 0; i < nr; i++) {
    g.add(box(mats, 'metal_thin', w, 0.095, 0.06, 0, h - 0.06 - i * 0.108, -0.04, { bevel: 0.015 }));
  }
  // barra inferior mais grossa, levemente amassada
  g.add(box(mats, 'metal_thin', w + 0.05, 0.09, 0.09, 0, h - 0.06 - nr * 0.108, -0.04,
    { bevel: 0.02, rot: [0, 0, 0.006] }));
  return g;
}

// ---------------------------------------------------------------------------
// fachada e telhado
// ---------------------------------------------------------------------------

/** Ar-condicionado de janela/parede: corpo chanfrado, veneziana, grelha e suporte. */
export function makeAC(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 0.84, h = (o && o.h) || 0.6, d = (o && o.d) || 0.36;
  g.add(box(mats, 'metal_thin', w, h, d, 0, 0, 0, { bevel: 0.035 }));
  // veneziana frontal
  for (let i = 0; i < 6; i++) {
    g.add(box(mats, 'metal_thin', w * 0.52, 0.045, 0.05, -w * 0.2, h * 0.34 - i * 0.09, d * 0.5 + 0.01,
      { rot: [0.32, 0, 0], solid: false }));
  }
  // grelha do ventilador
  const fr = Math.min(h, w) * 0.34;
  g.add(ring(mats, 'metal_thin', fr, 0.022, w * 0.26, 0, d * 0.5 + 0.02,
    { upright: true, solid: false, seg: 16 }));
  const grille = new THREE.Group();
  for (let i = 0; i < 4; i++) {
    grille.add(box(mats, 'metal_thin', fr * 2, 0.018, 0.018, 0, 0, 0,
      { rot: [0, 0, (i * Math.PI) / 4], solid: false }));
  }
  grille.position.set(w * 0.26, 0, d * 0.5 + 0.02);
  g.add(grille);
  // suportes em L e dreno
  g.add(box(mats, 'metal_thin', 0.05, 0.05, d + 0.16, -w * 0.36, -h * 0.5 - 0.03, -0.06, { solid: false }));
  g.add(box(mats, 'metal_thin', 0.05, 0.05, d + 0.16, w * 0.36, -h * 0.5 - 0.03, -0.06, { solid: false }));
  g.add(box(mats, 'metal_thin', 0.05, 0.26, 0.05, -w * 0.36, -h * 0.5 - 0.16, d * 0.5 - 0.02, { solid: false }));
  g.add(box(mats, 'metal_thin', 0.05, 0.26, 0.05, w * 0.36, -h * 0.5 - 0.16, d * 0.5 - 0.02, { solid: false }));
  g.add(cyl(mats, 'metal_thin', 0.018, 0.018, 0.5, 6, w * 0.42, -h * 0.5 - 0.25, 0.02, { solid: false }));
  return g;
}

/** Tubo de descida com bracadeiras e joelho no pe. */
export function makeDownpipe(mats, o) {
  const g = new THREE.Group();
  const h = (o && o.h) || 6, r = (o && o.r) || 0.07;
  g.add(cyl(mats, 'metal_thin', r, r, h, 8, 0, h * 0.5, 0, { solid: false }));
  const n = Math.max(2, Math.floor(h / 2.2));
  for (let i = 0; i < n; i++) {
    g.add(box(mats, 'metal_thin', r * 3.2, 0.05, 0.14, 0, 0.6 + i * (h / n), -r, { solid: false }));
  }
  g.add(cyl(mats, 'metal_thin', r, r, 0.4, 8, 0, 0.16, 0.16,
    { rot: [Math.PI / 2.6, 0, 0], solid: false }));
  g.add(cyl(mats, 'metal_thin', r * 1.25, r * 1.25, 0.1, 8, 0, h - 0.2, 0, { solid: false }));
  return g;
}

/**
 * Antena parabolica com braco, LNB e mastro.
 *
 * O prato NAO pode usar 'metal_thin': aquele material e telha ONDULADA, e numa
 * lathe o U corre em volta do eixo - a ondulacao se comprime para menos de um
 * pixel perto do cubo e vira moire em leque. Chapa lisa ('metal') com uma unica
 * repeticao radial, e 24 segmentos para o disco parar de ler poligonal.
 */
export function makeDish(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 0.5;
  const pts = [];
  const seg = 5;
  for (let i = 0; i <= seg; i++) {
    const x = (i / seg) * r;
    pts.push(new THREE.Vector2(Math.max(0.001, x), (x * x) / (2.6 * r)));
  }
  for (let i = seg; i >= 0; i--) {
    const x = (i / seg) * r;
    pts.push(new THREE.Vector2(Math.max(0.001, x), (x * x) / (2.6 * r) - 0.025));
  }
  const gd = new THREE.LatheGeometry(pts, 24);
  const dish = part(mats, 'metal', gd, { solid: false, uv: 'keep', uvScale: [1, 1] });
  dish.rotation.x = -Math.PI / 2.35;
  dish.position.set(0, 0.55, 0);
  g.add(dish);
  g.add(cyl(mats, 'metal_thin', 0.02, 0.02, r * 1.15, 6, 0, 0.62, r * 0.5,
    { rot: [0.75, 0, 0], solid: false }));
  g.add(box(mats, 'metal_thin', 0.09, 0.09, 0.16, 0, 0.62 + r * 0.5, r * 0.86,
    { bevel: 0.02, solid: false }));
  g.add(cyl(mats, 'metal_thin', 0.035, 0.035, 0.62, 8, 0, 0.31, -0.06, { solid: false }));
  g.add(box(mats, 'metal_thin', 0.26, 0.05, 0.26, 0, 0.03, -0.06, { solid: false }));
  return g;
}

/** Antena de TV: mastro com travessas e dipolos. */
export function makeAntenna(mats, o) {
  const g = new THREE.Group();
  const h = (o && o.h) || 2.1;
  // mastro e dipolos PRECISAM projetar sombra: com o sol a 15 graus a antena
  // desenha um risco de 8 m na laje, e e esse risco que prende a antena no chao.
  g.add(cyl(mats, 'metal_thin', 0.026, 0.034, h, 6, 0, h * 0.5, 0, { solid: false }));
  for (let i = 0; i < 6; i++) {
    const y = h * 0.42 + i * (h * 0.09);
    const len = 1.0 - i * 0.12;
    g.add(box(mats, 'metal_thin', 0.022, 0.022, len, 0, y, 0, { solid: false }));
  }
  g.add(box(mats, 'metal_thin', 0.022, 0.022, 1.25, 0, h * 0.38, 0, { solid: false }));
  g.add(box(mats, 'metal_thin', 0.22, 0.05, 0.22, 0, 0.03, 0, { solid: false }));
  return g;
}

/** Caixa d'agua sobre estrutura de aco - marca registrada de laje no Oriente Medio. */
export function makeWaterTank(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 0.72, h = (o && o.h) || 1.25, legs = (o && o.legs) !== false;
  const base = legs ? 0.95 : 0.05;
  if (legs) {
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const px = Math.cos(a) * r * 0.78, pz = Math.sin(a) * r * 0.78;
      g.add(box(mats, 'metal_thin', 0.07, base, 0.07, px, base * 0.5, pz));
      g.add(box(mats, 'metal_thin', 0.05, 0.05, r * 1.3, px * 0.5, base * 0.5, pz * 0.5,
        { rot: [0, a, 0], solid: false }));
    }
    g.add(box(mats, 'metal_thin', r * 1.7, 0.07, r * 1.7, 0, base, 0, { bevel: 0.02 }));
  }
  g.add(cyl(mats, 'metal', r, r * 0.94, h, 12, 0, base + h * 0.5, 0, { bevel: 0 }));
  for (let i = 0; i < 2; i++) {
    g.add(cyl(mats, 'metal', r * 1.03, r * 1.03, 0.055, 12, 0, base + h * (0.3 + i * 0.36), 0,
      { solid: false }));
  }
  g.add(cyl(mats, 'metal', r * 0.34, r * 0.36, 0.14, 8, 0, base + h + 0.06, 0));
  g.add(cyl(mats, 'metal_thin', 0.045, 0.045, 0.9, 6, r * 0.72, base + h * 0.2, 0,
    { solid: false }));
  return g;
}

/**
 * Exaustor de telhado. Devolve o grupo com userData.fan apontando para a helice
 * (o Level mantem essa malha viva e gira ela em update - custo de 1 draw call).
 */
export function makeRoofVent(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 0.42;
  g.add(box(mats, 'metal_thin', r * 2.4, 0.14, r * 2.4, 0, 0.07, 0, { bevel: 0.03 }));
  g.add(cyl(mats, 'metal_thin', r, r * 1.06, 0.42, 14, 0, 0.35, 0));
  g.add(ring(mats, 'metal_thin', r * 1.02, 0.03, 0, 0.56, 0, { solid: false, seg: 16 }));
  // helice em UMA malha: ela fica viva (gira em update) e custa 1 draw call
  const blades = [];
  for (let i = 0; i < 4; i++) {
    const bg = new THREE.BoxGeometry(r * 1.5, 0.02, 0.14);
    bg.rotateX(0.5);
    bg.rotateY((i * Math.PI) / 2);
    blades.push(bg.toNonIndexed());
  }
  const hub = new THREE.CylinderGeometry(0.07, 0.07, 0.09, 8).toNonIndexed();
  blades.push(hub);
  const fanGeo = mergeGeometries(blades, false) || blades[0];
  const fan = part(mats, 'metal_thin', fanGeo,
    { solid: false, cast: false, uv: 'keep', uvScale: [2, 2], dynamic: true });
  fan.position.set(0, 0.52, 0);
  g.add(fan);
  g.userData.fan = fan;
  g.add(cyl(mats, 'metal_thin', 0.05, 0.05, 0.1, 8, 0, 0.57, 0, { solid: false }));
  return g;
}

/** Chamine/duto de tijolo com capuz - quebra a silhueta do telhado. */
export function makeChimney(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 0.6, h = (o && o.h) || 1.5;
  g.add(box(mats, 'brick', w, h, w, 0, h * 0.5, 0));
  g.add(box(mats, 'concrete', w + 0.16, 0.1, w + 0.16, 0, h + 0.05, 0, { bevel: 0.02 }));
  g.add(cyl(mats, 'metal_thin', w * 0.24, w * 0.24, 0.4, 10, 0, h + 0.28, 0));
  g.add(cyl(mats, 'metal_thin', w * 0.36, w * 0.2, 0.16, 10, 0, h + 0.55, 0, { solid: false }));
  return g;
}

// ---------------------------------------------------------------------------
// circulacao - escada, guarda-corpo, passarela, coluna
// ---------------------------------------------------------------------------

/**
 * Guarda-corpo metalico: montantes, corrimao, travessa e rodape.
 *
 * Em 'metal_dull' (aco pintado e gasto), nao em 'metal_thin': chapa galvanizada
 * espelhada faz o corrimao da passarela sair mais claro que o ceu no contraluz -
 * era o objeto mais brilhante do quadro em 07 e em 08.
 */
export function makeRailing(mats, o) {
  const g = new THREE.Group();
  const len = o.len, h = o.h || 1.05;
  const posts = Math.max(2, Math.round(len / 1.3) + 1);
  for (let i = 0; i < posts; i++) {
    const x = -len * 0.5 + (i * len) / (posts - 1);
    g.add(box(mats, 'metal_dull', 0.055, h, 0.055, x, h * 0.5, 0, { bevel: 0.012 }));
  }
  g.add(cyl(mats, 'metal_dull', 0.035, 0.035, len, 8, 0, h, 0, { rot: [0, 0, Math.PI / 2] }));
  g.add(box(mats, 'metal_dull', len, 0.035, 0.035, 0, h * 0.55, 0));
  if (o.toe !== false) g.add(box(mats, 'metal_dull', len, 0.13, 0.03, 0, 0.07, 0));
  if (o.mesh) {
    const nb = Math.max(4, Math.round(len / 0.16));
    for (let i = 0; i < nb; i++) {
      const x = -len * 0.5 + 0.06 + (i * (len - 0.12)) / (nb - 1);
      g.add(box(mats, 'metal_dull', 0.016, h - 0.16, 0.016, x, h * 0.5, 0,
        { solid: false, cast: false }));
    }
  }
  return g;
}

/**
 * Escada de concreto externa. Sobe em +Z. Degrau com focinho (nosing) saliente,
 * viga lateral cheia de um lado e guarda-corpo do outro.
 */
export function makeStairs(mats, o) {
  const g = new THREE.Group();
  const steps = o.steps, rise = o.rise, run = o.run, w = o.w || 1.6;
  const total = steps * rise;
  for (let i = 0; i < steps; i++) {
    const y = (i + 1) * rise, z = i * run;
    g.add(box(mats, 'concrete', w, 0.09, run, 0, y - 0.045, z + run * 0.5));   // piso
    g.add(box(mats, 'concrete', w, rise, 0.1, 0, y - rise * 0.5, z + 0.05));   // espelho
    g.add(box(mats, 'concrete', w + 0.04, 0.05, 0.07, 0, y - 0.02, z + run + 0.02,
      { bevel: 0.012 }));                                                       // focinho
  }
  // viga/mureta lateral cheia, seguindo a inclinacao
  const ang = Math.atan2(total, steps * run);
  const slen = Math.hypot(total, steps * run);
  g.add(box(mats, 'concrete', 0.22, 0.55, slen, -w * 0.5 - 0.11, total * 0.5 - 0.1, (steps * run) * 0.5,
    { rot: [-ang, 0, 0] }));
  g.add(box(mats, 'concrete', 0.26, total, 0.3, -w * 0.5 - 0.11, total * 0.5, steps * run - 0.15));
  if (o.railing !== false) {
    // DUAS rotacoes, nesta ordem, e por isso ha um grupo no meio:
    // makeRailing constroi o corrimao ao longo de X (os montantes sao
    // distribuidos em `x`), mas a escada sobe em +Z. So inclinar em X, como
    // estava, deixava a barra ATRAVESSADA na escada - um corrimao do
    // comprimento do lance inteiro cruzando a passagem no meio do caminho.
    // O giro em Y alinha o corrimao com a subida; o grupo externo entao aplica
    // a inclinacao no mesmo eixo e com o mesmo sinal da viga lateral acima.
    // Grupo em vez de dois angulos no mesmo Euler porque a ordem de aplicacao
    // do Euler importa aqui, e aninhar deixa a intencao explicita.
    const r = makeRailing(mats, { len: slen, h: 0.98, toe: false, mesh: true });
    r.rotation.y = Math.PI / 2;
    const rail = new THREE.Group();
    rail.rotation.x = -ang;
    rail.add(r);
    rail.position.set(w * 0.5 + 0.04, total * 0.5, (steps * run) * 0.5);
    g.add(rail);
  }
  if (o.landing) {
    g.add(box(mats, 'concrete', w + 0.5, 0.22, o.landing, 0, total - 0.11,
      steps * run + o.landing * 0.5));
  }
  return g;
}

/** Passarela de aco (grade + longarinas + guarda-corpo dos dois lados). */
export function makeCatwalk(mats, o) {
  const g = new THREE.Group();
  const len = o.len, w = o.w || 1.35;
  g.add(box(mats, 'metal_thin', w, 0.05, len, 0, 0, 0));
  const nr = Math.max(4, Math.round(len / 0.35));
  for (let i = 0; i < nr; i++) {
    g.add(box(mats, 'metal_thin', w, 0.035, 0.04, 0, 0.04, -len * 0.5 + (i * len) / (nr - 1),
      { solid: false }));
  }
  // longarinas em U
  for (const s of [-1, 1]) {
    g.add(box(mats, 'metal', 0.05, 0.24, len, s * (w * 0.5 - 0.03), -0.14, 0));
    g.add(box(mats, 'metal', 0.14, 0.05, len, s * (w * 0.5 - 0.06), -0.24, 0, { solid: false }));
    const r = makeRailing(mats, { len, h: 1.06, mesh: true });
    r.rotation.y = Math.PI / 2;
    r.position.set(s * (w * 0.5 - 0.02), 0.02, 0);
    g.add(r);
  }
  return g;
}

/** Coluna de arcada: base, fuste com friso e capitel. */
export function makeColumn(mats, o) {
  const g = new THREE.Group();
  const h = o.h, r = o.r || 0.24;
  g.add(box(mats, 'concrete', r * 2.6, 0.16, r * 2.6, 0, 0.08, 0, { bevel: 0.03 }));
  g.add(box(mats, 'concrete', r * 2.15, 0.1, r * 2.15, 0, 0.21, 0, { bevel: 0.025 }));
  g.add(box(mats, 'plaster', r * 2, h - 0.55, r * 2, 0, 0.26 + (h - 0.55) * 0.5, 0, { bevel: 0.035 }));
  g.add(box(mats, 'concrete', r * 2.28, 0.09, r * 2.28, 0, h - 0.26, 0, { bevel: 0.02 }));
  g.add(box(mats, 'concrete', r * 2.6, 0.17, r * 2.6, 0, h - 0.09, 0, { bevel: 0.035 }));
  return g;
}

// ---------------------------------------------------------------------------
// cobertura leve - toldo, varal, barraca
// ---------------------------------------------------------------------------

/**
 * Toldo de lona com ESTRUTURA VISIVEL POR BAIXO.
 *
 * O jogador de FPS passa por baixo do toldo - e por baixo que a peca e julgada,
 * e ali nao havia braco, tubo frontal nem mao-francesa: a lona brotava da
 * alvenaria e ficava no ar. Agora sempre existem, sob a lona e para dentro do
 * vao (nao escondidos na borda): dois bracos, um tubo frontal grosso, tirantes
 * ate a parede e caibros intermediarios a cada ~1,1 m.
 *
 * A geometria tambem varia por instancia (projecao, inclinacao, fase da listra e
 * 1 em cada 4 com a lona rasgada) - quatro abas identicas no mesmo quadro eram o
 * que mais denunciava kit copiado.
 */
export function makeAwning(mats, o) {
  const g = new THREE.Group();
  const w = o.w || 3.2;
  const rng = o.rng || makeRng(Math.round((w * 977 + (o.d || 1.5) * 613 + (o.drop || 0.55) * 411)) | 0);
  const d = (o.d || 1.5) * rand(rng, 0.86, 1.24);
  const drop = (o.drop || 0.55) * rand(rng, 0.78, 1.34);
  const slope = Math.hypot(d, drop);
  const ang = Math.atan2(drop, d);
  const torn = rng() < 0.25;

  // lona: plano com barriga entre os caibros
  const pg = new THREE.PlaneGeometry(w, slope, 6, 3);
  const pos = pg.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const u = pos.getX(i) / w, v = pos.getY(i);
    let z = Math.cos(u * Math.PI * 2.0) * 0.05 - 0.03 + Math.sin(v * 2.2) * 0.02;
    // rasgo: a ponta de um dos lados cai e afunda
    if (torn && u > 0.24 && v < -slope * 0.2) z -= (u - 0.24) * 0.9;
    pos.setZ(i, z);
  }
  pg.computeVertexNormals();
  pg.rotateX(-Math.PI / 2 + ang);
  pg.translate(0, -drop * 0.5, d * 0.5);
  offsetUV(pg, rand(rng, 0, 4), rand(rng, 0, 4));
  cloth(g, mats, 'fabric', pg, { uvScale: [w / 0.62, slope / 0.62] });

  // bambolina com dentes (recorte classico), altura irregular
  const teeth = Math.max(4, Math.round(w / 0.34));
  for (let i = 0; i < teeth; i++) {
    const x = -w * 0.5 + (i + 0.5) * (w / teeth);
    const dep = torn && i > teeth * 0.62 ? 0.05 : rand(rng, 0.14, 0.24);
    const sh = new THREE.Shape();
    sh.moveTo(-w / teeth / 2, 0);
    sh.lineTo(w / teeth / 2, 0);
    sh.lineTo(0, -dep);
    sh.lineTo(-w / teeth / 2, 0);
    const tg = new THREE.ShapeGeometry(sh);
    tg.translate(x, -drop, d);
    offsetUV(tg, rand(rng, 0, 3), rand(rng, 0, 3));
    cloth(g, mats, 'fabric', tg, { uvScale: [1.2, 1.2] });
  }

  // --- estrutura, toda ela visivel de baixo -------------------------------
  // tubo frontal grosso, ABAIXO da lona (e o que le como toldo montado)
  g.add(cyl(mats, 'metal_dull', 0.038, 0.038, w + 0.12, 8, 0, -drop - 0.045, d - 0.015,
    { rot: [0, 0, Math.PI / 2], solid: false }));
  // barra de fixacao na parede + duas chapas de apoio
  g.add(box(mats, 'metal_dull', w + 0.06, 0.07, 0.07, 0, 0.02, 0.03, { solid: false }));
  for (const s of [-1, 1]) {
    g.add(box(mats, 'metal_dull', 0.14, 0.2, 0.05, s * (w * 0.5 - 0.12), -0.04, 0.02,
      { solid: false }));
  }
  // caibros: os dois das pontas e mais um a cada ~1,1 m, todos SOB a lona.
  // rotX(+ang) leva o eixo +Z para (0, -sin, cos) = para baixo e para a frente,
  // que e o sentido real do caimento (com o sinal trocado o caibro subia e
  // atravessava a lona - era assim que estava).
  const rafters = Math.max(2, Math.round(w / 1.1) + 1);
  for (let i = 0; i < rafters; i++) {
    const x = -w * 0.5 + 0.06 + (i * (w - 0.12)) / (rafters - 1);
    g.add(box(mats, 'metal_dull', 0.04, 0.045, slope, x, -drop * 0.5 - 0.05, d * 0.5,
      { rot: [ang, 0, 0], solid: false }));
  }
  // mao-francesa: da alvenaria (embaixo) ate o tubo frontal, dentro do vao
  for (const s of [-1, 1]) {
    const bx = s * w * 0.29;
    const bl = Math.hypot(d, 0.375);
    g.add(box(mats, 'metal_dull', 0.035, 0.035, bl, bx, -drop - 0.23, d * 0.5,
      { rot: [-Math.atan2(0.375, d), 0, 0], solid: false }));
    g.add(box(mats, 'metal_dull', 0.06, 0.16, 0.05, bx, -drop - 0.4, 0.03, { solid: false }));
    // tirante fino de cima, prendendo o bordo na alvenaria
    const tl = Math.hypot(d, drop + 0.18);
    g.add(box(mats, 'metal_dull', 0.018, 0.018, tl, bx * 0.62, (0.15 - drop - 0.03) * 0.5,
      d * 0.5, { rot: [Math.atan2(drop + 0.18, d), 0, 0], solid: false }));
  }
  return g;
}

/**
 * Varal com roupas penduradas - silhueta e cor no beco, custo baixo.
 *
 * A corda ATRAVESSA o vao: ela nasce 12 cm antes de -len/2 e morre 12 cm depois
 * de +len/2, com uma bucha de fixacao em cada ponta. Quem chama tem que dar um
 * len igual a largura do beco - varal centrado num vao mais largo que ele fica
 * com as duas pontas presas no ar, e a roupa parece levitar.
 */
export function makeLaundryLine(mats, o) {
  const g = new THREE.Group();
  const len = o.len, rng = o.rng || makeRng(9);
  const ext = len * 0.5 + 0.12;
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(-ext, 0.02, 0),
    new THREE.Vector3(-len * 0.25, -len * 0.026, 0),
    new THREE.Vector3(0, -len * 0.035, 0),
    new THREE.Vector3(len * 0.25, -len * 0.026, 0),
    new THREE.Vector3(ext, 0.02, 0)
  ]);
  // 2 cm de raio: 1,2 cm sumia visualmente a partir de uns 8 m e sobrava so o
  // pano boiando. E a corda projeta sombra - e o risco no chao que a ancora.
  g.add(part(mats, 'metal_thin', new THREE.TubeGeometry(curve, 12, 0.02, 5, false),
    { solid: false, uv: 'keep', uvScale: [8, 1] }));
  // buchas de fixacao nas duas pontas, cravadas na parede
  for (const s of [-1, 1]) {
    g.add(box(mats, 'metal_thin', 0.07, 0.07, 0.16, s * (len * 0.5 + 0.04), 0.02, 0,
      { solid: false, bevel: 0.015 }));
    g.add(box(mats, 'metal_thin', 0.05, 0.16, 0.05, s * (len * 0.5 + 0.02), -0.06, 0,
      { solid: false }));
  }
  /**
   * As pecas. Antes eram oito retangulos IDENTICOS: mesma silhueta, mesma barra
   * inferior reta, mesma listra na mesma fase (uvScale fixo [1.4,2] independente
   * do tamanho), todos coplanares - lia como bandeirola de festa. Agora sao tres
   * silhuetas (camisa com mangas, calca com duas pernas, lencol dobrado SOBRE a
   * corda), cada uma torcida em Y, com bainha ondulada, UV proporcional ao
   * tamanho real e fase de listra propria. O tom alterna entre dois materiais
   * (lona colorida e estopa clara) - roupa de varal e colorida, e e isso que faz
   * a peca ler como roupa.
   */
  const n = Math.max(3, Math.floor(len / 0.82));
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const x = -len * 0.5 + t * len + rand(rng, -0.05, 0.05);
    const sag = -Math.sin(t * Math.PI) * len * 0.035;
    const kind = i % 3 === 0 ? 2 : (rng() > 0.5 ? 0 : 1);
    const matName = rng() > 0.45 ? 'fabric' : 'sandbag';
    const yaw = rand(rng, -0.38, 0.38);
    const parts = [];

    if (kind === 0) {
      // camisa: corpo + duas mangas caidas
      const cw = rand(rng, 0.42, 0.56), ch = rand(rng, 0.5, 0.72);
      const body = clothPanel(cw, ch, rng, { hem: 0.05 });
      body.translate(0, -ch * 0.5 - 0.04, 0);
      parts.push(body);
      for (const s of [-1, 1]) {
        const sw = rand(rng, 0.16, 0.24), shh = rand(rng, 0.2, 0.3);
        const sl = clothPanel(sw, shh, rng, { hem: 0.03, billow: 0.02 });
        sl.rotateZ(s * rand(rng, 0.35, 0.8));
        sl.translate(s * (cw * 0.5 + sw * 0.34), -0.16 - shh * 0.4, s * 0.02);
        parts.push(sl);
      }
    } else if (kind === 1) {
      // calca: cos curto e duas pernas com a fenda no meio
      const cw = rand(rng, 0.36, 0.48), ch = rand(rng, 0.62, 0.92);
      const waist = clothPanel(cw, ch * 0.34, rng, { hem: 0, segY: 2 });
      waist.translate(0, -ch * 0.17 - 0.04, 0);
      parts.push(waist);
      for (const s of [-1, 1]) {
        const lw = cw * 0.44;
        const leg = clothPanel(lw, ch * 0.7, rng, { hem: 0.045, segX: 3 });
        leg.rotateZ(s * rand(rng, 0.01, 0.09));
        leg.translate(s * (cw * 0.5 - lw * 0.5), -ch * 0.34 - ch * 0.35 - 0.04, s * 0.012);
        parts.push(leg);
      }
    } else {
      // lencol dobrado sobre a corda: duas metades caindo, uma mais curta
      const cw = rand(rng, 0.6, 0.86);
      for (const s of [-1, 1]) {
        const ch = rand(rng, 0.5, 0.86) * (s < 0 ? 1 : 0.82);
        const half = clothPanel(cw, ch, rng, { hem: 0.07 });
        half.translate(0, -ch * 0.5 - 0.015, s * 0.035);
        parts.push(half);
      }
    }

    let geo = parts.length === 1 ? parts[0] : mergeGeometries(parts, false);
    if (!geo) geo = parts[0];
    geo.rotateY(yaw);
    geo.translate(x, sag, 0);
    offsetUV(geo, rand(rng, 0, 5), rand(rng, 0, 5));
    cloth(g, mats, matName, geo, { uvScale: [1.9, 1.9] });
    for (let k = 0; k < parts.length; k++) {
      if (parts[k] !== geo) parts[k].dispose();
    }
    // pregadores
    const pw = kind === 2 ? 0.3 : 0.2;
    for (const s of [-1, 1]) {
      g.add(box(mats, 'wood', 0.025, 0.085, 0.03, x + s * pw, sag - 0.02, 0.02,
        { solid: false, cast: false }));
    }
  }
  return g;
}

/**
 * Barraca de feira: bancada, quatro pes, cobertura de lona e MERCADORIA.
 *
 * Bancada vazia le como andaime de montagem, nao como feira - era exatamente o
 * que acontecia. O que transforma a estrutura em comercio e a carga: caixinhas
 * e trouxas sobre o tampo, sacos na prateleira de baixo e um pano caindo da
 * barra da frente. Sao ~60 triangulos por barraca, tudo mesclado pelo batcher.
 */
export function makeMarketStall(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 2.4, d = (o && o.d) || 1.5, h = 0.92;
  const rng = (o && o.rng) || makeRng(17);
  g.add(box(mats, 'wood', w, 0.07, d, 0, h, 0, { bevel: 0.015 }));
  g.add(box(mats, 'wood', w, 0.1, 0.06, 0, h - 0.1, d * 0.5 - 0.03));
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(box(mats, 'wood', 0.075, h, 0.075, sx * (w * 0.5 - 0.08), h * 0.5, sz * (d * 0.5 - 0.08)));
      g.add(box(mats, 'metal_thin', 0.05, 2.05, 0.05, sx * (w * 0.5 - 0.05), 1.02,
        sz * (d * 0.5 - 0.05)));
    }
  }
  g.add(box(mats, 'wood', w - 0.16, 0.06, d - 0.16, 0, 0.28, 0, { solid: false }));

  // --- mercadoria sobre a bancada ----------------------------------------
  const nGoods = 6 + Math.floor(rng() * 4);
  for (let i = 0; i < nGoods; i++) {
    const gw = rand(rng, 0.12, 0.3);
    const gh = rand(rng, 0.1, 0.26);
    const gd = rand(rng, 0.12, 0.28);
    const mat = rng() > 0.45 ? 'wood' : 'fabric';
    g.add(box(mats, mat, gw, gh, gd,
      rand(rng, -w * 0.42, w * 0.42), h + 0.035 + gh * 0.5, rand(rng, -d * 0.3, d * 0.3),
      { rot: [0, rand(rng, -0.5, 0.5), 0], bevel: 0.02, solid: false }));
  }
  // trouxas na prateleira de baixo
  for (let i = 0; i < 3; i++) {
    const s = new RoundedBoxGeometry(rand(rng, 0.34, 0.46), 0.28, rand(rng, 0.26, 0.34), 1, 0.075);
    const m = part(mats, 'sandbag', s, { solid: false });
    m.position.set(-w * 0.3 + i * (w * 0.3), 0.45, rand(rng, -0.1, 0.1));
    m.rotation.y = rand(rng, -0.3, 0.3);
    g.add(m);
  }
  // pano caindo da barra da frente, com a mesma barriga do toldo
  const fg = new THREE.PlaneGeometry(w * 0.62, 0.9, 4, 3);
  const fp = fg.attributes.position;
  for (let i = 0; i < fp.count; i++) {
    fp.setZ(i, Math.cos((fp.getX(i) / (w * 0.62)) * 3.6) * 0.05 - 0.02);
  }
  fg.computeVertexNormals();
  fg.translate(-w * 0.1, h - 0.55, d * 0.5 + 0.04);
  cloth(g, mats, 'fabric', fg, { uvScale: [2, 2] });

  // cobertura em duas aguas
  for (const s of [-1, 1]) {
    const pg = new THREE.PlaneGeometry(w + 0.4, d * 0.62, 4, 2);
    pg.rotateX(-Math.PI / 2 + s * 0.42);
    pg.translate(0, 2.16 - 0.11, s * d * 0.27);
    cloth(g, mats, 'fabric', pg, { uvScale: [3, 2] });
  }
  g.add(box(mats, 'metal_thin', w + 0.4, 0.05, 0.05, 0, 2.2, 0, { solid: false }));
  return g;
}

/**
 * VEGETACAO: palmeira de rua e folhagem em geral.
 *
 * O que existia lia como recorte de papel colado na cena. Cada folha era um
 * poligono chapado de verde uniforme; o foliolo era UM triangulo por lado (o
 * serrote em zigue-zague que aparece em metade dos prints); nao havia variacao
 * entre folha e folha, nem ao longo da mesma folha, nem espessura, nem - o que
 * mais entrega folha de papel - NENHUMA translucidez. Ao lado de tijolo e
 * concreto com PBR de verdade, era o objeto que denunciava a cena.
 *
 * O que passou a existir:
 *  1. FOLIOLO COM VOLUME. Cada foliolo e uma tira DOBRADA na nervura (crista no
 *     meio, bordas mais baixas): quatro triangulos, duas meias-laminas com
 *     inclinacao diferente. E a dobra que faz o foliolo pegar dois valores de
 *     luz em vez de um chapado.
 *  2. FOLIOLOS EM GRUPO, dois por ponto de insercao, cada um com seu angulo de
 *     queda, largura e comprimento - o foliolo equidistante e o que fazia a
 *     borda da folha sair serrilhada como dente de serra.
 *  3. QUADRO MOVEL na raque (tangente, normal, lateral) que TORCE ao longo do
 *     comprimento: a folha deixa de ser plana, e duas folhas nunca sao
 *     coplanares.
 *  4. IDADE por folha (0 nova, 1 velha). Folha velha e mais longa, arqueia bem
 *     mais, e mais clara e mais amarela; folha nova fica quase em pe no centro
 *     da copa. Cada uma arqueia com curvatura propria.
 *  5. COR POR VERTICE: escurece no interior da copa (oclusao assada), clareia e
 *     amarela da base para a ponta do foliolo, resseca na borda, com jitter por
 *     foliolo. Nada de tom unico.
 *  6. TRANSLUCIDEZ: material 'leaf' (montado no Level) e de dupla face e soma um
 *     termo de backlight no shader, modulado pela COR DO VERTICE - folha clara e
 *     folha velha transluzem mais. Sem isso todo contraluz vira recorte preto.
 *  7. TRONCO: uma malha helicoidal com cicatrizes de bainha em losango, no lugar
 *     do cilindro liso com aneis empilhados por cima.
 */

/** Paleta da folhagem, em hex sRGB (o THREE.Color converte para linear). */
const LEAF_PAL = {
  base: 0x38491a,     // verde de folha adulta na sombra da copa
  sun: 0x5f6f28,      // verde de folha exposta
  old: 0x8a8340,      // folha velha, ja amarelando
  tip: 0xa79f57,      // ponta, sempre mais clara e mais seca
  edge: 0x7c6d3c,     // borda do foliolo, ressecada
  rachis: 0x7d6c38,   // raque/bainha, palha
  dry: 0x6f5c35,      // folha seca pendurada sob a coroa
  dryTip: 0x8a7444,
  weed: 0x5a5730,     // mato de floreira
  weedTip: 0x8e8145
};

const TAU = Math.PI * 2;
const _tmpColor = new THREE.Color();

/** Hex sRGB -> [r,g,b] no espaco de trabalho (linear). */
function linOf(hex, out) {
  _tmpColor.setHex(hex);
  out[0] = _tmpColor.r;
  out[1] = _tmpColor.g;
  out[2] = _tmpColor.b;
  return out;
}

/** out = a + (b - a) * t, componente a componente. */
function mix3(a, b, t, out) {
  out[0] = a[0] + (b[0] - a[0]) * t;
  out[1] = a[1] + (b[1] - a[1]) * t;
  out[2] = a[2] + (b[2] - a[2]) * t;
  return out;
}

function scale3(a, k, out) {
  out[0] = a[0] * k;
  out[1] = a[1] * k;
  out[2] = a[2] * k;
  return out;
}

/**
 * Acumulador de folhagem: posicao, normal, uv e COR por vertice.
 *
 * A cor entra na geometria porque o cenario todo e mesclado por material: nao
 * existe um material por folha onde pintar tom diferente. Cor por vertice e o
 * unico jeito de ter variacao entre folha e folha dentro de UM draw call - e ela
 * ainda serve de segunda funcao, modulando a translucidez no shader.
 */
class FoliageBuf {
  constructor() {
    this.p = [];
    this.n = [];
    this.t = [];
    this.c = [];
  }

  /**
   * Triangulo com normal COERENTE COM O WINDING.
   *
   * Obrigatorio: em material de dupla face o three inverte a normal por
   * gl_FrontFacing, entao normal apontando contra o winding sai com o
   * sombreado trocado (a face iluminada escurece). Quando o winding esta ao
   * contrario do `up` da folha, troca-se dois vertices em vez de virar a
   * normal. Depois a normal e amaciada 30% na direcao de `up`: sem isso cada
   * meia-lamina e uma faceta solta e a copa le como mosaico.
   */
  tri(a, b, c, up, uva, uvb, uvc, ca, cb, cc) {
    let p1 = b;
    let p2 = c;
    let t1 = uvb;
    let t2 = uvc;
    let k1 = cb;
    let k2 = cc;
    let nx = (p1[1] - a[1]) * (p2[2] - a[2]) - (p1[2] - a[2]) * (p2[1] - a[1]);
    let ny = (p1[2] - a[2]) * (p2[0] - a[0]) - (p1[0] - a[0]) * (p2[2] - a[2]);
    let nz = (p1[0] - a[0]) * (p2[1] - a[1]) - (p1[1] - a[1]) * (p2[0] - a[0]);
    if (nx * up[0] + ny * up[1] + nz * up[2] < 0) {
      p1 = c; p2 = b; t1 = uvc; t2 = uvb; k1 = cc; k2 = cb;
      nx = -nx; ny = -ny; nz = -nz;
    }
    let len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len < 1e-9) return;
    nx /= len; ny /= len; nz /= len;
    nx += (up[0] - nx) * 0.3;
    ny += (up[1] - ny) * 0.3;
    nz += (up[2] - nz) * 0.3;
    len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    nx /= len; ny /= len; nz /= len;
    const p = this.p, n = this.n, t = this.t, k = this.c;
    p.push(a[0], a[1], a[2], p1[0], p1[1], p1[2], p2[0], p2[1], p2[2]);
    n.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    t.push(uva[0], uva[1], t1[0], t1[1], t2[0], t2[1]);
    k.push(ca[0], ca[1], ca[2], k1[0], k1[1], k1[2], k2[0], k2[1], k2[2]);
  }

  geometry() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.p), 3));
    g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(this.n), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(this.t), 2));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.c), 3));
    return g;
  }
}

/**
 * Nome do material de folhagem. NAO existe na MaterialLibrary: e montado pelo
 * Level (dupla face, cor por vertice, backlight no shader). Toda geometria
 * empurrada com este nome PRECISA ter o atributo `color` - o Level mescla por
 * material, e um bin com geometria com e sem cor nao mescla.
 */
export const LEAF_MAT = 'leaf';

/**
 * Empurra folhagem para o batcher. Uma malha so: o material e de dupla face,
 * entao nao se duplica a geometria invertida como cloth() faz com pano.
 */
export function foliage(group, mats, geometry, opts) {
  if (!geometry.attributes.color) {
    // rede de seguranca: sem cor a geometria quebraria o merge do bin
    const n = geometry.attributes.position.count;
    const c = new Float32Array(n * 3);
    c.fill(1);
    geometry.setAttribute('color', new THREE.BufferAttribute(c, 3));
  }
  const o = Object.assign({ uv: 'keep', solid: false }, opts || {});
  group.add(part(mats, LEAF_MAT, geometry, o));
}

/**
 * Lamina simples afunilada, com dobra e leve curva - mato de floreira e
 * folhagem rasteira. Nasce na origem, crescendo em +Y.
 */
export function bladeGeometry(w, h, rng, bodyHex, tipHex) {
  const buf = new FoliageBuf();
  const body = linOf(bodyHex, [0, 0, 0]);
  const tipC = linOf(tipHex, [0, 0, 0]);
  const bend = rand(rng, -0.32, 0.32);
  const jit = rand(rng, 0.86, 1.14);
  const cBase = scale3(body, jit * 0.82, [0, 0, 0]);
  const cMid = mix3(body, tipC, 0.35, [0, 0, 0]);
  const cTip = mix3(body, tipC, 0.9, [0, 0, 0]);
  scale3(cMid, jit, cMid);
  scale3(cTip, jit, cTip);
  const up = [0, 0, 1];
  const seg = 3;
  const prev = [-w * 0.5, 0, 0];
  const prevR = [w * 0.5, 0, 0];
  let pl = prev, pr = prevR;
  let cl = cBase;
  for (let i = 1; i <= seg; i++) {
    const t = i / seg;
    const hw = w * 0.5 * (1 - t * 0.86);
    const y = h * t;
    const z = bend * h * t * t;
    const nl = [-hw, y, z];
    const nr = [hw, y, z];
    const cc = t < 0.6 ? mix3(cBase, cMid, t / 0.6, [0, 0, 0]) : mix3(cMid, cTip, (t - 0.6) / 0.4, [0, 0, 0]);
    buf.tri(pl, pr, nr, up, [0, (i - 1) / seg], [1, (i - 1) / seg], [1, t], cl, cl, cc);
    buf.tri(pl, nr, nl, up, [0, (i - 1) / seg], [1, t], [0, t], cl, cc, cc);
    pl = nl; pr = nr; cl = cc;
  }
  return buf.geometry();
}
/**
 * Folha PINADA de palmeira, em uma unica BufferGeometry nao indexada.
 *
 * O que existia era um PlaneGeometry inteiro: uma lamina lisa de 2 m por 40 cm.
 * Palmeira nao tem lamina lisa - tem raque com dezenas de foliolos, e e esse
 * recorte que faz a silhueta. Contra o ceu, a lamina macica virava espada de
 * agave; e o que o olho pegava primeiro em metade dos prints.
 *
 * Construida em X (0 na base, len na ponta). A raque usa um frame que torce
 * ao longo do arco. Cada ponto recebe dois foliolos independentes, feitos como
 * tiras segmentadas com crista central e bordas rebaixadas.
 */
function palmFrondGeometry(len, wid, rng, age, dry) {
  const buf = new FoliageBuf();
  const leafAge = Math.max(0, Math.min(1, age || 0));
  const isDry = !!dry;
  const nL = 13 + Math.floor(rng() * 4);
  const lift = 0.42 - leafAge * 0.1;
  const arch = rand(rng, 0.42, 0.52) + leafAge * rand(rng, 0.48, 0.62);
  const wave = rand(rng, -0.025, 0.025) * len;
  const twistStart = rand(rng, -0.24, 0.24);
  const twistSpan = rand(rng, 0.72, 1.18) + leafAge * 0.48;
  const green0 = linOf(LEAF_PAL.base, [0, 0, 0]);
  const green1 = linOf(LEAF_PAL.sun, [0, 0, 0]);
  const old = linOf(LEAF_PAL.old, [0, 0, 0]);
  const tip = linOf(LEAF_PAL.tip, [0, 0, 0]);
  const edge = linOf(LEAF_PAL.edge, [0, 0, 0]);
  const dry0 = linOf(LEAF_PAL.dry, [0, 0, 0]);
  const dry1 = linOf(LEAF_PAL.dryTip, [0, 0, 0]);
  const rachis = linOf(LEAF_PAL.rachis, [0, 0, 0]);

  function frame(u) {
    const phase = u * Math.PI * 1.7;
    const c = [len * u, len * (lift * u - arch * u * u), wave * Math.sin(phase)];
    let tx = len;
    let ty = len * (lift - 2 * arch * u);
    let tz = wave * Math.PI * 1.7 * Math.cos(phase);
    let tl = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
    tx /= tl; ty /= tl; tz /= tl;
    let sx = -tz;
    let sy = 0;
    let sz = tx;
    let sl = Math.sqrt(sx * sx + sz * sz) || 1;
    sx /= sl; sz /= sl;
    const ux = sy * tz - sz * ty;
    const uy = sz * tx - sx * tz;
    const uz = sx * ty - sy * tx;
    const twist = twistStart + twistSpan * u + Math.sin(u * Math.PI) * 0.18;
    const ct = Math.cos(twist);
    const st = Math.sin(twist);
    return {
      c,
      t: [tx, ty, tz],
      s: [sx * ct + ux * st, sy * ct + uy * st, sz * ct + uz * st],
      u: [ux * ct - sx * st, uy * ct - sy * st, uz * ct - sz * st]
    };
  }

  function colorAt(u, v, edgeMix, tone) {
    if (isDry) {
      const c = mix3(dry0, dry1, Math.min(1, u * 0.35 + v * 0.8), [0, 0, 0]);
      if (edgeMix > 0) mix3(c, edge, edgeMix * 0.45, c);
      return scale3(c, tone, c);
    }
    const c = mix3(green0, green1, Math.min(1, 0.12 + u * 0.52 + v * 0.22), [0, 0, 0]);
    mix3(c, old, leafAge * (0.18 + u * 0.42 + v * 0.22), c);
    const yellow = Math.max(0, u * 0.42 + v * 0.72 - 0.42);
    mix3(c, tip, Math.min(0.78, yellow * (0.34 + leafAge * 0.54)), c);
    if (edgeMix > 0) mix3(c, edge, edgeMix * (0.12 + leafAge * 0.12), c);
    return scale3(c, tone * (0.74 + u * 0.16 + v * 0.12), c);
  }

  // The rachis follows the same twisted frame used by every leaflet pair.
  const rachisSeg = 14;
  for (let i = 0; i < rachisSeg; i++) {
    const u0 = i / rachisSeg;
    const u1 = (i + 1) / rachisSeg;
    const f0 = frame(u0);
    const f1 = frame(u1);
    const hw0 = 0.034 * (1 - u0 * 0.72) + 0.005;
    const hw1 = 0.034 * (1 - u1 * 0.72) + 0.005;
    const a = [f0.c[0] - f0.s[0] * hw0, f0.c[1] - f0.s[1] * hw0, f0.c[2] - f0.s[2] * hw0];
    const b = [f0.c[0] + f0.s[0] * hw0, f0.c[1] + f0.s[1] * hw0, f0.c[2] + f0.s[2] * hw0];
    const c = [f1.c[0] + f1.s[0] * hw1, f1.c[1] + f1.s[1] * hw1, f1.c[2] + f1.s[2] * hw1];
    const d = [f1.c[0] - f1.s[0] * hw1, f1.c[1] - f1.s[1] * hw1, f1.c[2] - f1.s[2] * hw1];
    const rc0 = isDry ? mix3(dry0, dry1, u0 * 0.45, [0, 0, 0]) : mix3(rachis, old, leafAge * u0 * 0.35, [0, 0, 0]);
    const rc1 = isDry ? mix3(dry0, dry1, u1 * 0.45, [0, 0, 0]) : mix3(rachis, old, leafAge * u1 * 0.35, [0, 0, 0]);
    buf.tri(a, b, c, f0.u, [u0 * 2, 0], [u0 * 2, 0.1], [u1 * 2, 0.1], rc0, rc0, rc1);
    buf.tri(a, c, d, f0.u, [u0 * 2, 0], [u1 * 2, 0.1], [u1 * 2, 0], rc0, rc1, rc1);
  }

  // Exactly two independent leaflets grow at every insertion point.
  for (let i = 0; i < nL; i++) {
    const u = 0.075 + (i / (nL - 1)) * 0.88;
    const f = frame(u);
    const prof = Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.04)), 0.7);
    for (let sideIndex = 0; sideIndex < 2; sideIndex++) {
      const sideSign = sideIndex === 0 ? -1 : 1;
      const leafletLen = wid * (0.42 + prof * 0.82) * rand(rng, 0.84, 1.14);
      const leafletWid = leafletLen * rand(rng, 0.105, 0.16);
      const sweep = rand(rng, 0.16, 0.34) + u * rand(rng, 0.12, 0.25);
      const elevation = rand(rng, -0.11, 0.13) - leafAge * rand(rng, 0.02, 0.09);
      let dx = f.s[0] * sideSign * Math.cos(sweep) + f.t[0] * Math.sin(sweep) + f.u[0] * elevation;
      let dy = f.s[1] * sideSign * Math.cos(sweep) + f.t[1] * Math.sin(sweep) + f.u[1] * elevation;
      let dz = f.s[2] * sideSign * Math.cos(sweep) + f.t[2] * Math.sin(sweep) + f.u[2] * elevation;
      let dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      dx /= dl; dy /= dl; dz /= dl;
      let bx = f.u[1] * dz - f.u[2] * dy;
      let by = f.u[2] * dx - f.u[0] * dz;
      let bz = f.u[0] * dy - f.u[1] * dx;
      let bl = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
      bx /= bl; by /= bl; bz /= bl;
      const rootOffset = 0.026 + 0.018 * (1 - u);
      const root = [
        f.c[0] + f.s[0] * sideSign * rootOffset,
        f.c[1] + f.s[1] * sideSign * rootOffset,
        f.c[2] + f.s[2] * sideSign * rootOffset
      ];
      const tone = rand(rng, 0.9, 1.08);
      const stripSeg = 3;
      let prevL = null;
      let prevM = null;
      let prevR = null;
      let prevEdge = null;
      let prevMid = null;
      for (let j = 0; j <= stripSeg; j++) {
        const v = j / stripSeg;
        const widthProfile = 0.05 + Math.pow(Math.sin(Math.PI * v), 0.72) * 0.95;
        const halfW = leafletWid * 0.5 * widthProfile;
        const fold = leafletWid * (0.16 + leafAge * 0.05) * Math.sin(Math.PI * v);
        const fall = leafletLen * (0.035 + leafAge * 0.055) * v * v;
        const center = [
          root[0] + dx * leafletLen * v - f.u[0] * fall,
          root[1] + dy * leafletLen * v - f.u[1] * fall,
          root[2] + dz * leafletLen * v - f.u[2] * fall
        ];
        const mid = [center[0] + f.u[0] * fold, center[1] + f.u[1] * fold, center[2] + f.u[2] * fold];
        const left = [center[0] + bx * halfW - f.u[0] * fold * 0.3, center[1] + by * halfW - f.u[1] * fold * 0.3, center[2] + bz * halfW - f.u[2] * fold * 0.3];
        const right = [center[0] - bx * halfW - f.u[0] * fold * 0.3, center[1] - by * halfW - f.u[1] * fold * 0.3, center[2] - bz * halfW - f.u[2] * fold * 0.3];
        const edgeColor = colorAt(u, v, 1, tone);
        const midColor = colorAt(u, v, 0, tone);
        if (j > 0) {
          const v0 = (j - 1) / stripSeg;
          buf.tri(prevL, prevM, mid, f.u, [0, v0], [0.5, v0], [0.5, v], prevEdge, prevMid, midColor);
          buf.tri(prevL, mid, left, f.u, [0, v0], [0.5, v], [0, v], prevEdge, midColor, edgeColor);
          buf.tri(prevM, prevR, right, f.u, [0.5, v0], [1, v0], [1, v], prevMid, prevEdge, edgeColor);
          buf.tri(prevM, right, mid, f.u, [0.5, v0], [1, v], [0.5, v], prevMid, edgeColor, midColor);
        }
        prevL = left;
        prevM = mid;
        prevR = right;
        prevEdge = edgeColor;
        prevMid = midColor;
      }
    }
  }

  return buf.geometry();
}

export function makePalm(mats, o) {
  const g = new THREE.Group();
  const h = (o && o.h) || 5.2;
  const rng = (o && o.rng) || makeRng(303);
  const lean = (o && o.lean) !== undefined ? o.lean : rand(rng, -0.08, 0.08);
  const segs = 6;

  // tronco: cilindros empilhados com raio decrescente e leve inclinacao
  for (let i = 0; i < segs; i++) {
    const t0 = i / segs, t1 = (i + 1) / segs;
    const y0 = t0 * h, y1 = t1 * h;
    const r0 = 0.21 - t0 * 0.075, r1 = 0.21 - t1 * 0.075;
    g.add(cyl(mats, 'wood', r1, r0, y1 - y0, 9, lean * y0 * 0.5, (y0 + y1) * 0.5, 0,
      { solid: i < 2 }));
  }
  /**
   * Cicatrizes do tronco em ANEIS, nao em escamas de 10 cm em espiral: as
   * escamas antigas sumiam a 5 m e o tronco virava poste marrom liso. Anel
   * horizontal de 4 cm de saliencia le a 30 m porque pega a luz rasante na
   * borda de cima e joga sombra na de baixo - e a leitura classica de palmeira.
   */
  const rings = 11;
  for (let i = 0; i < rings; i++) {
    const t = 0.05 + (i / rings) * 0.9;
    const y = t * h;
    const rr = 0.21 - t * 0.075;
    const bulge = (i % 2 === 0) ? 0.042 : 0.026;
    g.add(cyl(mats, 'wood', rr + bulge * 0.6, rr + bulge, 0.1 + (i % 3) * 0.03, 9,
      lean * y * 0.5, y, 0, { solid: false, rot: [0.03 * (i % 2 ? 1 : -1), i * 0.7, 0] }));
  }
  // coroa
  const cy = h + 0.08;
  const cx = lean * h * 0.5;
  g.add(cyl(mats, 'wood', 0.19, 0.12, 0.3, 9, cx, cy - 0.08, 0, { solid: false }));

  /**
   * Copa de verdade: folhas novas ficam curtas e eretas; folhas velhas ficam
   * longas, baixas, arqueadas e amarelas. As idades sao espalhadas no azimute.
   */
  const nf = 17 + Math.floor(rng() * 4);
  for (let i = 0; i < nf; i++) {
    const a = (i / nf) * Math.PI * 2 + rand(rng, -0.14, 0.14);
    const ageRank = (i * 7) % nf;
    const age = Math.max(0, Math.min(1, ageRank / (nf - 1) + rand(rng, -0.035, 0.035)));
    const len = rand(rng, 2.48, 2.78) + age * rand(rng, 0.68, 0.96);
    const wid = rand(rng, 0.45, 0.57) * (0.92 + age * 0.12);
    const fg = palmFrondGeometry(len, wid, rng, age, false);
    const pitch = 0.76 - age * 0.9 + rand(rng, -0.11, 0.11);
    fg.rotateZ(pitch);
    fg.rotateY(a);
    fg.translate(cx, cy, 0);
    foliage(g, mats, fg);
  }
  // folhas secas caidas rente ao tronco, sob a coroa
  for (let i = 0; i < 5; i++) {
    const a = i * 1.27;
    const dg = palmFrondGeometry(rand(rng, 0.9, 1.5), 0.3, rng, 1, true);
    dg.rotateZ(-rand(rng, 0.9, 1.35));
    dg.rotateY(a);
    dg.translate(cx, cy - 0.22, 0);
    foliage(g, mats, dg, { cast: false });
  }
  // cacho de dactilos preso na coroa
  for (let i = 0; i < 2; i++) {
    const a = i * 2.6 + 0.7;
    g.add(box(mats, 'grass', 0.16, 0.36, 0.16, cx + Math.cos(a) * 0.24, cy - 0.3,
      Math.sin(a) * 0.24, { rot: [0.24, a, 0.3], bevel: 0.05, solid: false }));
  }
  return g;
}

// ---------------------------------------------------------------------------
// obstaculos e cobertura de combate
// ---------------------------------------------------------------------------

/** Contentor maritimo: nervuras, cantoneiras, portas com barras de travamento. */
export function makeContainer(mats, o) {
  const g = new THREE.Group();
  const len = (o && o.len) || 6.06, w = (o && o.w) || 2.44, h = (o && o.h) || 2.59;
  const mat = (o && o.mat) || 'metal';

  g.add(box(mats, mat, len - 0.16, h - 0.3, w - 0.16, 0, h * 0.5, 0));
  // nervuras verticais nas laterais
  const nr = Math.floor((len - 0.8) / 0.31);
  for (let i = 0; i < nr; i++) {
    const x = -len * 0.5 + 0.55 + i * 0.31;
    for (const s of [-1, 1]) {
      g.add(box(mats, mat, 0.16, h - 0.42, 0.07, x, h * 0.5, s * (w * 0.5 - 0.05),
        { solid: false, bevel: 0.02 }));
    }
  }
  // travessas superior/inferior e cantoneiras
  for (const s of [-1, 1]) {
    g.add(box(mats, mat, len, 0.16, 0.14, 0, h - 0.08, s * (w * 0.5 - 0.05), { bevel: 0.025 }));
    g.add(box(mats, mat, len, 0.16, 0.14, 0, 0.09, s * (w * 0.5 - 0.05), { bevel: 0.025 }));
    g.add(box(mats, mat, 0.14, h, 0.16, s * (len * 0.5 - 0.05), h * 0.5, 0, { bevel: 0.025 }));
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(box(mats, 'metal', 0.2, 0.2, 0.2, sx * (len * 0.5 - 0.09), 0.1, sz * (w * 0.5 - 0.09),
        { bevel: 0.04 }));
      g.add(box(mats, 'metal', 0.2, 0.2, 0.2, sx * (len * 0.5 - 0.09), h - 0.1, sz * (w * 0.5 - 0.09),
        { bevel: 0.04 }));
    }
  }
  // teto levemente abaulado
  g.add(box(mats, mat, len - 0.2, 0.07, w - 0.2, 0, h - 0.03, 0, { solid: false }));
  // portas em uma ponta
  const dx = len * 0.5 - 0.06;
  for (const s of [-1, 1]) {
    g.add(box(mats, mat, 0.08, h - 0.36, w * 0.5 - 0.12, dx, h * 0.5, s * (w * 0.25),
      { bevel: 0.015 }));
    for (let i = 0; i < 2; i++) {
      g.add(cyl(mats, 'metal', 0.035, 0.035, h - 0.5, 8, dx + 0.06, h * 0.5,
        s * (w * 0.25) + (i === 0 ? -0.28 : 0.28), { solid: false }));
      g.add(box(mats, 'metal', 0.07, 0.1, 0.11, dx + 0.06, h * 0.52,
        s * (w * 0.25) + (i === 0 ? -0.28 : 0.28), { solid: false }));
    }
    g.add(box(mats, 'metal', 0.06, 0.09, 0.28, dx + 0.08, h * 0.5 - 0.18, s * (w * 0.25) + 0.02,
      { solid: false, rot: [0, 0, 0.35] }));
  }
  return g;
}

/**
 * Muro de sacos de areia.
 *
 * O que existia era uma prateleira de garrafas: cilindros de raio constante,
 * todos do mesmo tamanho, no mesmo tom, alinhados com espacamento regular. O que
 * define saco de areia e o contrario - barriga irregular, o de cima achatando o
 * de baixo, tom variando (uns encardidos, uns quase limpos), a fiada de cima
 * recuada e um ou dois sacos caidos fora da pilha, com areia escapando no pe.
 */
export function makeSandbagWall(mats, o) {
  const g = new THREE.Group();
  const len = o.len, rows = o.rows || 4, rng = o.rng || makeRng(21);
  const bw = 0.5, bh = 0.21, bd = 0.32;
  const geo = new RoundedBoxGeometry(bw, bh, bd, 1, 0.085);
  const bag = (x, y, z, sx, sy, sz, rx, ry, rz, dirty) => {
    const m = part(mats, dirty ? 'dirt' : 'sandbag', geo.clone(), {});
    m.position.set(x, y, z);
    m.rotation.set(rx, ry, rz);
    m.scale.set(sx, sy, sz);
    g.add(m);
    return m;
  };

  let yTop = 0;
  for (let r = 0; r < rows; r++) {
    // peso: a fiada de baixo achata e a de cima aperta 3,5 cm para dentro
    const squash = 0.82 + (r / Math.max(1, rows - 1)) * 0.2;
    const inset = r * 0.035;
    const y = bh * 0.5 * squash + yTop;
    const off = (r % 2) * bw * 0.5;
    const n = Math.max(1, Math.floor((len - off) / bw));
    for (let i = 0; i < n; i++) {
      bag(
        -len * 0.5 + off + bw * (i + 0.5) + rand(rng, -0.04, 0.04),
        y + rand(rng, -0.012, 0.012),
        rand(rng, -0.045, 0.045) + (r % 2 ? 0.02 : -0.02),
        rand(rng, 0.88, 1.12), squash * rand(rng, 0.92, 1.08),
        rand(rng, 0.86, 1.14) * (1 - inset),
        rand(rng, -0.09, 0.09), rand(rng, -0.26, 0.26), rand(rng, -0.1, 0.1),
        rng() > 0.68
      );
    }
    yTop = y + bh * 0.47 * squash;
  }
  // sacos soltos no pe da barricada e areia escapando
  const loose = 1 + Math.floor(rng() * 2);
  for (let i = 0; i < loose; i++) {
    const s = rng() > 0.5 ? 1 : -1;
    bag(rand(rng, -len * 0.42, len * 0.42), bh * 0.42, s * rand(rng, 0.28, 0.46),
      rand(rng, 0.9, 1.1), 0.86, rand(rng, 0.9, 1.1),
      rand(rng, -0.2, 0.2), rand(rng, 0, 3.1), rand(rng, -0.3, 0.3), rng() > 0.5);
  }
  // areia escapando no pe (material 'dirt': ja existe em todas as regioes, entao
  // o acumulo nao custa um bin novo)
  g.add(cyl(mats, 'dirt', len * 0.34, len * 0.46, 0.07, 9,
    rand(rng, -len * 0.2, len * 0.2), 0.03, rand(rng, -0.1, 0.1),
    { solid: false, cast: false }));
  return g;
}

/** Barreira New Jersey (perfil extrudado, com quinas quebradas). */
export function makeJerseyBarrier(mats, o) {
  const len = (o && o.len) || 2.4;
  const s = new THREE.Shape();
  s.moveTo(-0.31, 0);
  s.lineTo(0.31, 0);
  s.lineTo(0.24, 0.09);
  s.lineTo(0.13, 0.36);
  s.lineTo(0.1, 0.82);
  s.lineTo(-0.1, 0.82);
  s.lineTo(-0.13, 0.36);
  s.lineTo(-0.24, 0.09);
  s.lineTo(-0.31, 0);
  const g = new THREE.Group();
  const m = extrude(mats, 'concrete', s, len, { bevel: 0.025 });
  m.rotation.y = Math.PI / 2;
  g.add(m);
  // alcas de icamento
  for (const sx of [-1, 1]) {
    g.add(ring(mats, 'metal_thin', 0.06, 0.014, sx * len * 0.24, 0.83, 0,
      { solid: false, seg: 10 }));
  }
  return g;
}

/**
 * Tambor de 200 L com cintas de rolamento, aro e batoque.
 *
 * variant: 0 em pe (padrao), 1 amassado (escala nao uniforme e um aro fora do
 * lugar), 2 deitado no chao. Um unico modelo repetido 25 vezes pelo mapa e a
 * assinatura mais rapida de cenario montado com kit minimo.
 */
export function makeBarrel(mats, o) {
  const g = new THREE.Group();
  const body = new THREE.Group();
  const r = (o && o.r) || 0.29, h = (o && o.h) || 0.88;
  const mat = (o && o.mat) || 'metal';
  const variant = (o && o.variant) | 0;
  const dent = variant === 1;
  body.add(cyl(mats, mat, r, r, h, 14, 0, h * 0.5, 0));
  for (let i = 0; i < 2; i++) {
    const off = dent && i === 1 ? 0.09 : 0;
    body.add(cyl(mats, mat, r * 1.06, r * 1.06, 0.06, 14, 0, h * (0.33 + i * 0.34) + off, 0,
      { solid: false, rot: dent && i === 1 ? [0.08, 0, 0.05] : null }));
  }
  body.add(cyl(mats, mat, r * 1.05, r * 1.05, 0.05, 14, 0, h - 0.03, 0, { solid: false }));
  body.add(cyl(mats, mat, r * 1.05, r * 1.05, 0.05, 14, 0, 0.03, 0, { solid: false }));
  if (!(o && o.open)) {
    body.add(cyl(mats, mat, 0.055, 0.055, 0.035, 8, r * 0.55, h + 0.005, 0, { solid: false }));
  } else {
    body.add(cyl(mats, mat, r * 0.9, r * 0.9, 0.05, 14, 0, h - 0.32, 0, { solid: false }));
  }
  if (dent) body.scale.set(1.0, 0.94, 1.07);
  if (variant === 2) {
    // deitado: gira em Z e apoia a lateral no chao
    body.rotation.z = Math.PI / 2;
    body.position.set(h * 0.5 - 0.02, r, 0);
  }
  g.add(body);
  return g;
}

/**
 * Engradado. variant escolhe o desenho:
 *   0 caixote de tabuas (padrao)
 *   1 mesmo caixote com duas travessas em diagonal na face
 *   2 caixa plastica de feira, chanfro grande, sem sarrafo ('rubber')
 *   3 caixote aberto, sem tampa, com palha aparecendo
 * O Level sorteia no ponto de uso com o rng local.
 */
export function makeCrate(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 0.86, h = (o && o.h) || 0.72, d = (o && o.d) || 0.86;
  const variant = (o && o.variant) | 0;
  const t = 0.06;

  if (variant === 2) {
    // caixa plastica: casca com chanfro largo, aba superior e vazados laterais
    g.add(box(mats, 'rubber', w, h, d, 0, h * 0.5, 0, { bevel: 0.055 }));
    g.add(box(mats, 'rubber', w + 0.06, 0.05, d + 0.06, 0, h - 0.02, 0, { bevel: 0.02 }));
    g.add(box(mats, 'rubber', w + 0.05, 0.05, d + 0.05, 0, 0.04, 0, { bevel: 0.02, solid: false }));
    for (let i = 0; i < 3; i++) {
      const yy = h * (0.28 + i * 0.22);
      g.add(box(mats, 'rubber', w * 0.62, 0.035, d + 0.03, 0, yy, 0, { solid: false }));
    }
    return g;
  }

  const open = variant === 3;
  g.add(box(mats, 'wood', w - t, h - t, d - t, 0, h * 0.5, 0));
  for (const sy of [-1, 1]) {
    if (open && sy > 0) continue;   // caixote aberto: sem a moldura de cima
    g.add(box(mats, 'wood', w, t, t, 0, h * 0.5 + sy * (h * 0.5 - t * 0.5), d * 0.5 - t * 0.5,
      { bevel: 0.012 }));
    g.add(box(mats, 'wood', w, t, t, 0, h * 0.5 + sy * (h * 0.5 - t * 0.5), -d * 0.5 + t * 0.5,
      { bevel: 0.012 }));
    g.add(box(mats, 'wood', t, t, d, w * 0.5 - t * 0.5, h * 0.5 + sy * (h * 0.5 - t * 0.5), 0,
      { bevel: 0.012 }));
    g.add(box(mats, 'wood', t, t, d, -w * 0.5 + t * 0.5, h * 0.5 + sy * (h * 0.5 - t * 0.5), 0,
      { bevel: 0.012 }));
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(box(mats, 'wood', t, h, t, sx * (w * 0.5 - t * 0.5), h * 0.5, sz * (d * 0.5 - t * 0.5),
        { bevel: 0.012 }));
    }
  }
  // ripa central em cada face grande
  g.add(box(mats, 'wood', w - t, 0.05, 0.03, 0, h * 0.5, d * 0.5 - 0.01, { solid: false }));
  g.add(box(mats, 'wood', w - t, 0.05, 0.03, 0, h * 0.5, -d * 0.5 + 0.01, { solid: false }));
  g.add(box(mats, 'wood', 0.03, 0.05, d - t, w * 0.5 - 0.01, h * 0.5, 0, { solid: false }));

  if (variant === 1) {
    // travessas em X na face da frente e nas costas
    for (const sz of [-1, 1]) {
      for (const a of [0.72, -0.72]) {
        g.add(box(mats, 'wood', Math.hypot(w, h) * 0.92, 0.075, 0.028, 0, h * 0.5,
          sz * (d * 0.5 + 0.005), { rot: [0, 0, a], bevel: 0.01, solid: false }));
      }
    }
  } else if (open) {
    // palha saindo pela boca do caixote
    const rng = (o && o.rng) || makeRng(88);
    for (let i = 0; i < 3; i++) {
      const pg = new THREE.PlaneGeometry(w * 0.85, 0.3, 3, 1);
      const p = pg.attributes.position;
      for (let k = 0; k < p.count; k++) {
        p.setZ(k, Math.sin((p.getX(k) / w) * 6.0 + i) * 0.05);
      }
      pg.computeVertexNormals();
      pg.rotateX(-Math.PI / 2 + rand(rng, -0.3, 0.3));
      pg.rotateY(rand(rng, 0, 3.1));
      pg.translate(rand(rng, -0.08, 0.08), h - 0.03 + i * 0.035, rand(rng, -0.08, 0.08));
      cloth(g, mats, 'fabric', pg, { uvScale: [1.6, 1] });
    }
  }
  return g;
}

/** Palete de madeira (chao de galpao). */
export function makePallet(mats) {
  const g = new THREE.Group();
  for (let i = 0; i < 3; i++) {
    g.add(box(mats, 'wood', 0.1, 0.09, 1.15, -0.45 + i * 0.45, 0.045, 0));
  }
  for (let i = 0; i < 6; i++) {
    g.add(box(mats, 'wood', 1.15, 0.02, 0.12, 0, 0.1, -0.5 + i * 0.2,
      { rot: [0, Math.PI / 2, 0], solid: false }));
  }
  return g;
}

/** Caveira de lixo metalica com tampa inclinada e rodizios. */
export function makeDumpster(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 1.9, h = (o && o.h) || 1.15, d = (o && o.d) || 1.05;
  const s = new THREE.Shape();
  s.moveTo(-w * 0.42, 0.12);
  s.lineTo(w * 0.42, 0.12);
  s.lineTo(w * 0.5, h);
  s.lineTo(-w * 0.5, h);
  s.lineTo(-w * 0.42, 0.12);
  const body = extrude(mats, 'metal', s, d, { bevel: 0.02 });
  g.add(body);
  g.add(box(mats, 'metal', w * 1.02, 0.07, d + 0.04, 0, h + 0.02, 0, { bevel: 0.02 }));
  for (const sz of [-1, 1]) {
    const lid = box(mats, 'metal_thin', w * 0.99, 0.05, d * 0.52, 0, h + 0.12, sz * d * 0.26,
      { rot: [sz * 0.13, 0, 0], bevel: 0.015 });
    g.add(lid);
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(cyl(mats, 'rubber', 0.09, 0.09, 0.05, 10, sx * w * 0.36, 0.07, sz * d * 0.34,
        { rot: [0, 0, Math.PI / 2], solid: false }));
    }
    g.add(box(mats, 'metal', 0.06, 0.1, d * 0.5, sx * w * 0.47, h * 0.62, 0, { solid: false }));
  }
  return g;
}

/** Pneu solto (empilhavel). */
export function makeTire(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 0.33;
  const t = new THREE.TorusGeometry(r, 0.12, 6, 16);
  const m = part(mats, 'rubber', t, { uv: 'keep', uvScale: [6, 1.5] });
  m.rotation.x = Math.PI / 2;
  m.position.y = 0.12;
  g.add(m);
  return g;
}

// ---------------------------------------------------------------------------
// rua - poste, fios, veiculo, entulho
// ---------------------------------------------------------------------------

/** Poste de concreto com cruzeta, isoladores, luminaria e transformador. */
export function makePole(mats, o) {
  const g = new THREE.Group();
  const h = (o && o.h) || 8.2;
  g.add(cyl(mats, 'concrete', 0.11, 0.19, h, 8, 0, h * 0.5, 0));
  g.add(cyl(mats, 'concrete', 0.26, 0.28, 0.24, 10, 0, 0.12, 0));
  // degraus de escalada
  for (let i = 0; i < 6; i++) {
    g.add(box(mats, 'metal_thin', 0.24, 0.03, 0.03, 0, 1.6 + i * 0.6, 0,
      { rot: [0, (i % 2) * Math.PI * 0.5, 0], solid: false, cast: false }));
  }
  // cruzeta com isoladores
  const cy = h - 0.55;
  g.add(box(mats, 'wood', 0.11, 0.13, 2.1, 0, cy, 0, { bevel: 0.02 }));
  g.add(box(mats, 'metal_thin', 0.05, 0.5, 0.05, 0, cy - 0.28, 0.42,
    { rot: [0.6, 0, 0], solid: false }));
  for (let i = 0; i < 4; i++) {
    const z = -0.82 + i * 0.55;
    g.add(cyl(mats, 'glass', 0.055, 0.075, 0.13, 8, 0, cy + 0.14, z, { solid: false }));
    g.add(cyl(mats, 'metal_thin', 0.02, 0.02, 0.12, 6, 0, cy + 0.05, z, { solid: false }));
  }
  if (!o || o.lamp !== false) {
    /**
     * A luminaria vai DENTRO do grupo do braco, em coordenada local.
     *
     * Antes ela era posicionada a mao em (0, h-0.82, 1.38) enquanto a ponta real
     * do braco caia em (0, h-1.043, 1.421): sobravam 15,8 cm de AR entre o tubo e
     * a base da luminaria, em todos os postes do mapa. Pendurando no braco, o
     * conjunto acompanha qualquer mudanca de angulo e a emenda nao pode abrir.
     */
    const tilt = Math.PI / 2 - 0.28;
    const half = 0.75;
    const tipY = Math.cos(tilt) * half;      // 0,2073
    const tipZ = Math.sin(tilt) * half;      // 0,7207
    const arm = new THREE.Group();
    arm.add(cyl(mats, 'metal_dull', 0.04, 0.045, half * 2, 8, 0, 0, 0,
      { rot: [tilt, 0, 0], solid: false }));
    // bracadeira: liga a ponta do tubo ao corpo da luminaria
    arm.add(box(mats, 'metal_dull', 0.09, 0.13, 0.09, 0, tipY - 0.045, tipZ + 0.03,
      { solid: false, bevel: 0.02 }));
    const head = new THREE.Group();
    head.add(box(mats, 'metal_dull', 0.28, 0.13, 0.62, 0, 0, 0, { bevel: 0.045, solid: false }));
    head.add(box(mats, 'glass', 0.22, 0.05, 0.5, 0, -0.08, 0, { solid: false, cast: false }));
    head.position.set(0, tipY - 0.13, tipZ + 0.19);
    head.rotation.x = 0.1;
    arm.add(head);
    arm.position.set(0, h - 1.25, 0.7);
    g.add(arm);
  }
  if (o && o.transformer) {
    g.add(cyl(mats, 'metal', 0.29, 0.29, 0.72, 12, 0.42, h - 2.1, 0, { solid: false }));
    g.add(cyl(mats, 'metal', 0.31, 0.31, 0.06, 12, 0.42, h - 1.75, 0, { solid: false }));
    g.add(box(mats, 'metal_thin', 0.5, 0.08, 0.08, 0.22, h - 1.78, 0, { solid: false }));
  }
  return g;
}

/** Fio suspenso entre dois pontos, com barriga (catenaria simplificada). */
export function makeWire(mats, ax, ay, az, bx, by, bz, sag, r) {
  const mid = new THREE.Vector3((ax + bx) * 0.5, (ay + by) * 0.5 - (sag || 0.6), (az + bz) * 0.5);
  const q1 = new THREE.Vector3(ax + (bx - ax) * 0.25, ay + (by - ay) * 0.25 - (sag || 0.6) * 0.62,
    az + (bz - az) * 0.25);
  const q2 = new THREE.Vector3(ax + (bx - ax) * 0.75, ay + (by - ay) * 0.75 - (sag || 0.6) * 0.62,
    az + (bz - az) * 0.75);
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(ax, ay, az), q1, mid, q2, new THREE.Vector3(bx, by, bz)
  ]);
  const geo = new THREE.TubeGeometry(curve, 14, r || 0.022, 4, false);
  return part(mats, 'metal_thin', geo,
    { solid: false, cast: false, receive: false, uv: 'keep', uvScale: [14, 1] });
}

const SEDAN_PROFILE = [
  [-2.16, 0.34], [-2.22, 0.66], [-2.12, 0.96], [-1.42, 1.03], [-0.98, 1.44],
  [-0.34, 1.52], [0.52, 1.5], [1.0, 1.06], [1.72, 0.96], [2.14, 0.86], [2.22, 0.6], [2.16, 0.33]
];
const PICKUP_PROFILE = [
  [-2.5, 0.36], [-2.55, 0.98], [-1.0, 1.02], [-0.95, 1.55], [-0.3, 1.62],
  [0.62, 1.6], [1.02, 1.1], [1.8, 1.0], [2.2, 0.9], [2.28, 0.62], [2.2, 0.34]
];

/**
 * Veiculo abandonado. A lataria e um perfil lateral extrudado com bisel e
 * caixas de roda ARQUEADAS (nada de tijolo com rodas). Vidros sao chapas
 * escuras aplicadas por fora - com o carro queimado isso le certo.
 */
export function makeVehicle(mats, o) {
  const g = new THREE.Group();
  const kind = (o && o.kind) || 'sedan';
  const prof = kind === 'pickup' ? PICKUP_PROFILE : SEDAN_PROFILE;
  const width = kind === 'pickup' ? 1.94 : 1.78;
  const bodyMat = (o && o.mat) || 'metal';
  const sh = new THREE.Shape();
  sh.moveTo(prof[0][0], prof[0][1]);
  for (let i = 1; i < prof.length; i++) sh.lineTo(prof[i][0], prof[i][1]);
  // volta pela base, abrindo as caixas de roda
  const wheelF = kind === 'pickup' ? 1.5 : 1.42;
  const wheelR = kind === 'pickup' ? -1.62 : -1.42;
  const ar = 0.44;
  sh.lineTo(wheelF + ar, 0.3);
  sh.absarc(wheelF, 0.3, ar, 0, Math.PI, false);
  sh.lineTo(wheelR + ar, 0.3);
  sh.absarc(wheelR, 0.3, ar, 0, Math.PI, false);
  sh.lineTo(prof[0][0], prof[0][1]);

  const body = extrude(mats, bodyMat, sh, width, { bevel: 0.05, curveSegments: 5 });
  g.add(body);

  // vidros escuros (laterais, para-brisa e vigia)
  const gz = width * 0.5 + 0.01;
  for (const s of [-1, 1]) {
    g.add(box(mats, 'glass', 1.36, 0.36, 0.02, -0.2, 1.24, s * gz,
      { solid: false, cast: false }));
  }
  g.add(box(mats, 'glass', 0.05, 0.52, width - 0.24, 0.78, 1.28, 0,
    { rot: [0, 0, 0.62], solid: false, cast: false }));
  g.add(box(mats, 'glass', 0.05, 0.46, width - 0.3, -1.06, 1.24, 0,
    { rot: [0, 0, -0.68], solid: false, cast: false }));

  // para-choques, faroletes, mocinhos de acabamento
  for (const s of [-1, 1]) {
    const x = s > 0 ? prof[prof.length - 2][0] + 0.06 : prof[1][0] - 0.06;
    g.add(box(mats, 'metal', 0.16, 0.2, width + 0.06, x, 0.56, 0, { bevel: 0.04 }));
    for (const sz of [-1, 1]) {
      g.add(box(mats, 'glass', 0.06, 0.14, 0.34, x - s * 0.05, 0.8, sz * (width * 0.5 - 0.28),
        { solid: false, cast: false }));
    }
  }
  g.add(box(mats, 'metal_thin', 0.14, 0.08, 0.22, 0.72, 1.16, width * 0.5 + 0.12,
    { bevel: 0.02, solid: false }));
  g.add(box(mats, 'metal_thin', 0.14, 0.08, 0.22, 0.72, 1.16, -width * 0.5 - 0.12,
    { bevel: 0.02, solid: false }));

  // rodas: uma murcha para nao ficar simetrico demais
  const flat = (o && o.flatTire) !== false;
  const wz = width * 0.5 - 0.06;
  const wheelPos = [[wheelF, wz], [wheelF, -wz], [wheelR, wz], [wheelR, -wz]];
  for (let i = 0; i < 4; i++) {
    const [wx, zz] = wheelPos[i];
    const squash = flat && i === 2 ? 0.62 : 1;
    const rr = 0.36;
    const t = cyl(mats, 'rubber', rr, rr, 0.26, 14, wx, rr * squash, zz,
      { rot: [Math.PI / 2, 0, 0] });
    t.scale.set(1, 1, squash);
    g.add(t);
    g.add(cyl(mats, 'metal', rr * 0.55, rr * 0.55, 0.28, 10, wx, rr * squash, zz,
      { rot: [Math.PI / 2, 0, 0], solid: false }));
  }

  if (o && o.doorOpen) {
    const leaf = new THREE.Group();
    leaf.add(box(mats, bodyMat, 1.0, 0.78, 0.07, 0.5, 0, 0, { bevel: 0.02 }));
    leaf.add(box(mats, 'glass', 0.86, 0.34, 0.03, 0.5, 0.55, 0, { solid: false, cast: false }));
    leaf.position.set(-0.5, 1.0, width * 0.5 - 0.03);
    leaf.rotation.y = -0.85;
    g.add(leaf);
  }
  if (o && o.burned) {
    // carcaca: capo levantado e ferragem exposta
    g.add(box(mats, 'metal_thin', 1.0, 0.05, width - 0.3, 1.5, 1.4, 0,
      { rot: [0, 0, -0.5], bevel: 0.015 }));
    for (let i = 0; i < 4; i++) {
      g.add(cyl(mats, 'metal_thin', 0.02, 0.02, 0.6, 5, -0.2 + i * 0.3, 1.6, -0.3 + i * 0.22,
        { rot: [0.4, i, 0.2], solid: false }));
    }
  }
  return g;
}

/**
 * Rampa de entulho: cunha solida (caminhavel) + blocos e vergalhoes soltos por
 * cima. Os blocos ficam baixos de proposito - degrau do jogador absorve.
 */
export function makeRubbleRamp(mats, o) {
  const g = new THREE.Group();
  const len = o.len, w = o.w, rise = o.rise, rng = o.rng || makeRng(77);
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(len, 0);
  s.lineTo(len, rise);
  s.lineTo(len - 0.35, rise);
  s.lineTo(0, 0.06);
  s.lineTo(0, 0);
  // a cunha sobe em +X e tem largura w em Z - as lascas soltas usam o mesmo eixo
  g.add(extrude(mats, 'concrete', s, w, { bevel: 0.02 }));
  for (let i = 0; i < 26; i++) {
    const t = rng();
    const x = t * len;
    const y = 0.05 + t * (rise - 0.1);
    const bw = rand(rng, 0.16, 0.42);
    g.add(box(mats, rng() > 0.45 ? 'brick' : 'concrete', bw, rand(rng, 0.07, 0.13),
      rand(rng, 0.16, 0.36), x, y, rand(rng, -w * 0.45, w * 0.45),
      { rot: [rand(rng, -0.3, 0.3), rand(rng, 0, 3.14), rand(rng, -0.2, 0.2)], solid: false }));
  }
  for (let i = 0; i < 5; i++) {
    g.add(cyl(mats, 'metal_thin', 0.014, 0.014, rand(rng, 0.5, 1.1), 5,
      rand(rng, len * 0.55, len * 0.95), rise * 0.75, rand(rng, -w * 0.4, w * 0.4),
      { rot: [rand(rng, -0.8, -0.2), rand(rng, 0, 3), rand(rng, -0.5, 0.5)], solid: false }));
  }
  // saia de espalhamento: desabamento joga material para fora do pe da cunha.
  // Sem ela a rampa encontra o chao numa linha reta e limpa e le como prop colado.
  scatterSkirt(g, mats, rng, -0.1, 0, w * 0.5, 3.0, 22, -1);
  g.add(cyl(mats, 'dirt', w * 0.44, w * 0.86, 0.03, 9, -0.5, 0.015, 0,
    { solid: false, cast: false }));
  return g;
}

/**
 * Cacos espalhados a partir de uma origem, com a densidade caindo com a
 * distancia. dirX = -1 espalha para -X (pe da rampa), 0 espalha em anel.
 */
function scatterSkirt(group, mats, rng, ox, oy, r0, reach, count, dirX) {
  for (let i = 0; i < count; i++) {
    // sqrt inverso: mais perto da base, poucas pecas longe
    const t = rng() * rng();
    const d = r0 + t * reach;
    const a = dirX < 0 ? rand(rng, Math.PI * 0.55, Math.PI * 1.45) : rng() * Math.PI * 2;
    const sc = 1.0 - t * 0.45;
    group.add(box(mats, rng() > 0.5 ? 'brick' : 'concrete',
      rand(rng, 0.1, 0.3) * sc, rand(rng, 0.05, 0.12) * sc, rand(rng, 0.1, 0.26) * sc,
      ox + Math.cos(a) * d, oy + 0.03, Math.sin(a) * d * 0.8,
      { rot: [rand(rng, -0.4, 0.4), rand(rng, 0, 3.14), rand(rng, -0.35, 0.35)],
        solid: false, cast: false }));
  }
}

/**
 * Monte de entulho decorativo (nao caminhavel, so silhueta e sujeira).
 * Com mat:'sand' e count:0 vira duna rasa - o acumulo que mata a aresta de 90
 * graus onde o chao encontra a parede.
 */
export function makeRubblePile(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 1.4, h = (o && o.h) || 0.6, rng = (o && o.rng) || makeRng(51);
  const base = (o && o.mat) || 'gravel';
  g.add(cyl(mats, base, r * 0.45, r, h, 9, 0, h * 0.5, 0, { cast: true }));
  const n = (o && o.count) === undefined ? 22 : o.count;
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * r * 1.05;
    g.add(box(mats, rng() > 0.5 ? 'concrete' : 'brick', rand(rng, 0.14, 0.4),
      rand(rng, 0.08, 0.2), rand(rng, 0.14, 0.36),
      Math.cos(a) * d, h * (1 - d / (r * 1.3)) * rand(rng, 0.4, 1.0) + 0.06, Math.sin(a) * d,
      { rot: [rand(rng, -0.5, 0.5), rand(rng, 0, 3.14), rand(rng, -0.4, 0.4)], solid: false }));
  }
  // duna de areia (count 0) nao espalha caco nenhum; monte de entulho sim
  if (n > 0 && (!o || o.skirt !== false)) {
    scatterSkirt(g, mats, rng, 0, 0, r * 0.9, r * 1.9, Math.max(8, Math.round(n * 0.7)), 0);
    // mancha de poeira clara projetada no piso, alem do limite do monte
    g.add(cyl(mats, 'dirt', r * 1.15, r * 1.75, 0.025, 10, rand(rng, -0.2, 0.2), 0.012,
      rand(rng, -0.2, 0.2), { solid: false, cast: false }));
  }
  return g;
}

/**
 * Alcapao de acesso a laje. Toda laje acessivel tem um - e a peca que explica
 * como se chega ali, e sozinha ja tira o telhado do estado de plano liso.
 */
export function makeRoofHatch(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 1.0, d = (o && o.d) || 0.95, h = (o && o.h) || 0.34;
  g.add(box(mats, 'concrete', w, h, d, 0, h * 0.5, 0, { bevel: 0.03 }));
  g.add(box(mats, 'concrete', w + 0.1, 0.07, d + 0.1, 0, h + 0.03, 0, { bevel: 0.02 }));
  // tampa metalica meio aberta, apoiada na aba
  const lid = new THREE.Group();
  lid.add(box(mats, 'metal_dull', w - 0.04, 0.05, d - 0.04, (w - 0.04) * 0.5, 0, 0,
    { bevel: 0.015, solid: false }));
  lid.position.set(-w * 0.5, h + 0.08, 0);
  lid.rotation.z = (o && o.open === false) ? 0.0 : 0.62;
  g.add(lid);
  g.add(box(mats, 'metal_dull', 0.05, 0.05, d - 0.1, -w * 0.5, h + 0.08, 0, { solid: false }));
  return g;
}

/** Tampa de bueiro / boca de lobo: disco de ferro fundido rente ao piso. */
export function makeManhole(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 0.33;
  g.add(cyl(mats, 'concrete', r + 0.09, r + 0.11, 0.06, 12, 0, 0.015, 0,
    { solid: false, cast: false }));
  g.add(cyl(mats, 'metal_dull', r, r, 0.05, 12, 0, 0.045, 0, { solid: false, cast: false }));
  for (let i = 0; i < 4; i++) {
    g.add(box(mats, 'metal_dull', r * 1.5, 0.02, 0.045, 0, 0.07, -r * 0.5 + i * (r / 3),
      { solid: false, cast: false }));
  }
  return g;
}

/** Grelha de sarjeta: moldura de concreto e barras de ferro. */
export function makeGutterGrate(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 0.5, l = (o && o.l) || 0.9;
  g.add(box(mats, 'concrete', w + 0.14, 0.08, l + 0.14, 0, 0.02, 0, { solid: false, cast: false }));
  for (let i = 0; i < 6; i++) {
    g.add(box(mats, 'metal_dull', w, 0.035, 0.05, 0, 0.055, -l * 0.5 + 0.08 + i * (l / 6),
      { solid: false, cast: false }));
  }
  return g;
}

/** Quad plano de 25 cm: papel/plastico solto. Geometria para InstancedMesh. */
export function paperGeometry() {
  const g = new THREE.PlaneGeometry(0.26, 0.19, 2, 2);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    p.setZ(i, Math.sin(p.getX(i) * 14) * 0.018 + Math.cos(p.getY(i) * 11) * 0.012);
  }
  g.computeVertexNormals();
  g.rotateX(-Math.PI / 2);
  return g;
}

/** Folha seca encurvada, para InstancedMesh. */
export function leafGeometry() {
  const s = new THREE.Shape();
  s.moveTo(-0.09, 0);
  s.quadraticCurveTo(0, 0.06, 0.1, 0);
  s.quadraticCurveTo(0, -0.05, -0.09, 0);
  const g = new THREE.ShapeGeometry(s, 5);
  g.rotateX(-Math.PI / 2);
  g.translate(0, 0.012, 0);
  return g;
}

/** Geometria de caco de tijolo para InstancedMesh (entulho espalhado). */
export function brickChunkGeometry() {
  const g = new THREE.BoxGeometry(0.22, 0.1, 0.11);
  return g;
}

/** Geometria de lasca de concreto para InstancedMesh. */
export function slabChunkGeometry() {
  const s = new THREE.Shape();
  s.moveTo(-0.24, -0.16);
  s.lineTo(0.26, -0.2);
  s.lineTo(0.3, 0.14);
  s.lineTo(-0.06, 0.24);
  s.lineTo(-0.28, 0.08);
  s.lineTo(-0.24, -0.16);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.07, bevelEnabled: false });
  g.rotateX(-Math.PI / 2);
  return g;
}

/** Placa comercial gasta, com moldura e bracos - preenche fachada morta. */
export function makeSign(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 2.6, h = (o && o.h) || 0.62;
  g.add(box(mats, 'metal_thin', w, h, 0.07, 0, 0, 0, { bevel: 0.02 }));
  g.add(box(mats, 'metal_thin', w + 0.08, 0.06, 0.1, 0, h * 0.5, 0, { solid: false }));
  g.add(box(mats, 'metal_thin', w + 0.08, 0.06, 0.1, 0, -h * 0.5, 0, { solid: false }));
  for (const s of [-1, 1]) {
    g.add(box(mats, 'metal_thin', 0.05, 0.05, 0.34, s * (w * 0.5 - 0.16), 0, -0.2,
      { solid: false }));
  }
  return g;
}

/** Estante metalica de galpao com caixas. */
export function makeShelf(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 2.2, h = (o && o.h) || 2.1, d = (o && o.d) || 0.6;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(box(mats, 'metal_thin', 0.07, h, 0.07, sx * (w * 0.5 - 0.05), h * 0.5,
        sz * (d * 0.5 - 0.05), { bevel: 0.015 }));
    }
  }
  for (let i = 0; i < 4; i++) {
    const y = 0.32 + i * (h - 0.42) / 3;
    g.add(box(mats, 'metal_thin', w, 0.045, d, 0, y, 0, { bevel: 0.01 }));
    g.add(box(mats, 'metal_thin', w, 0.09, 0.03, 0, y + 0.06, d * 0.5 - 0.02, { solid: false }));
  }
  return g;
}

/** Bancada de oficina com pes, gaveteiro e tampo marcado. */
export function makeWorkbench(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 2.4, d = 0.72, h = 0.9;
  g.add(box(mats, 'wood', w, 0.08, d, 0, h, 0, { bevel: 0.015 }));
  g.add(box(mats, 'metal_thin', w - 0.1, 0.06, d - 0.1, 0, h - 0.1, 0, { solid: false }));
  for (const sx of [-1, 1]) {
    g.add(box(mats, 'metal_thin', 0.07, h, 0.07, sx * (w * 0.5 - 0.08), h * 0.5, d * 0.5 - 0.08));
    g.add(box(mats, 'metal_thin', 0.07, h, 0.07, sx * (w * 0.5 - 0.08), h * 0.5, -d * 0.5 + 0.08));
  }
  g.add(box(mats, 'metal', w * 0.34, h - 0.22, d - 0.14, -w * 0.26, (h - 0.22) * 0.5, 0,
    { bevel: 0.02 }));
  for (let i = 0; i < 3; i++) {
    g.add(box(mats, 'metal_thin', w * 0.3, 0.04, 0.03, -w * 0.26, 0.22 + i * 0.22, d * 0.5 - 0.08,
      { solid: false }));
  }
  return g;
}

/** Lampada pendurada num fio - o Level balanca ela levemente. */
export function makeHangingLamp(mats, o) {
  const g = new THREE.Group();
  const len = (o && o.len) || 1.4;
  g.add(cyl(mats, 'metal_thin', 0.008, 0.008, len, 4, 0, -len * 0.5, 0,
    { solid: false, cast: false }));
  g.add(cyl(mats, 'metal_thin', 0.16, 0.05, 0.14, 12, 0, -len - 0.06, 0,
    { solid: false, cast: false }));
  g.add(cyl(mats, 'glass', 0.05, 0.05, 0.11, 8, 0, -len - 0.17, 0,
    { solid: false, cast: false }));
  return g;
}

/** Floreira de concreto com terra e mato seco. */
export function makePlanter(mats, o) {
  const g = new THREE.Group();
  const w = (o && o.w) || 1.5, d = (o && o.d) || 0.8, h = (o && o.h) || 0.55;
  g.add(box(mats, 'concrete', w, h, d, 0, h * 0.5, 0, { bevel: 0.03 }));
  g.add(box(mats, 'concrete', w + 0.09, 0.08, d + 0.09, 0, h - 0.03, 0, { bevel: 0.02 }));
  g.add(box(mats, 'dirt', w - 0.18, 0.1, d - 0.18, 0, h - 0.02, 0, { solid: false }));
  const rng = (o && o.rng) || makeRng(5);
  for (let i = 0; i < 9; i++) {
    const pg = new THREE.PlaneGeometry(rand(rng, 0.12, 0.24), rand(rng, 0.3, 0.6));
    pg.translate(0, rand(rng, 0.12, 0.28), 0);
    pg.rotateY(rng() * 3.14);
    pg.translate(rand(rng, -w * 0.35, w * 0.35), h - 0.02, rand(rng, -d * 0.3, d * 0.3));
    cloth(g, mats, 'grass', pg, { uvScale: [1, 1], cast: false });
  }
  return g;
}

/** Bebedouro/fonte seca do patio: bacia octogonal, bordo e pilar central. */
export function makeFountain(mats, o) {
  const g = new THREE.Group();
  const r = (o && o.r) || 2.2;
  g.add(cyl(mats, 'concrete', r, r * 1.04, 0.62, 8, 0, 0.31, 0, { bevel: 0 }));
  g.add(cyl(mats, 'concrete', r * 1.08, r * 1.08, 0.12, 8, 0, 0.66, 0, { bevel: 0 }));
  g.add(cyl(mats, 'tile', r * 0.9, r * 0.9, 0.5, 8, 0, 0.3, 0, { solid: false }));
  g.add(cyl(mats, 'dirt', r * 0.88, r * 0.88, 0.06, 8, 0, 0.36, 0, { solid: false }));
  g.add(cyl(mats, 'concrete', 0.22, 0.3, 1.15, 8, 0, 0.9, 0));
  g.add(cyl(mats, 'concrete', 0.42, 0.2, 0.16, 8, 0, 1.55, 0, { solid: false }));
  g.add(cyl(mats, 'metal_thin', 0.035, 0.035, 0.3, 6, 0, 1.72, 0, { solid: false }));
  return g;
}

/** Poco de luz falso: caixote empilhado com lona por cima (canto de patio). */
export function makeTarpStack(mats, o) {
  const g = new THREE.Group();
  const rng = (o && o.rng) || makeRng(33);
  const n = (o && o.count) || 4;
  for (let i = 0; i < n; i++) {
    const c = makeCrate(mats, { w: rand(rng, 0.7, 0.95), h: rand(rng, 0.55, 0.8),
      d: rand(rng, 0.7, 0.95) });
    c.position.set(rand(rng, -0.35, 0.35), i * 0.68, rand(rng, -0.3, 0.3));
    c.rotation.y = rand(rng, -0.4, 0.4);
    g.add(c);
  }
  const top = n * 0.68 + 0.1;
  const pg = new THREE.PlaneGeometry(1.9, 1.7, 5, 5);
  const p = pg.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const dx = p.getX(i) / 0.95, dy = p.getY(i) / 0.85;
    p.setZ(i, -Math.max(0, 1 - (dx * dx + dy * dy) * 0.55) * 0.22 + Math.sin(dx * 4) * 0.03);
  }
  pg.computeVertexNormals();
  pg.rotateX(-Math.PI / 2);
  pg.translate(0, top, 0);
  cloth(g, mats, 'fabric', pg, { uvScale: [2.5, 2.5] });
  return g;
}
